// DishSnap - Recipe Automation Dashboard

import { useState, useCallback, useEffect } from "react";
import { JobForm } from "./components/JobForm.js";
import { JobStatus } from "./components/JobStatus.js";
import { SearchTab } from "./components/SearchTab.js";
import { PinterestSearchTab } from "./components/PinterestSearchTab.js";
import { PinJobsTab } from "./components/PinJobsTab.js";
import { AnalyticsTab } from "./components/AnalyticsTab.js";
import { VideosTab } from "./components/VideosTab.js";
import { useJobPoller } from "./hooks/useJobPoller.js";
import type { JobResponse, JobSummary, JobListResponse, SiteConfig } from "@dishsnap/shared";
import styles from "./App.module.css";
import { apiUrl } from "./config";

type TabType = "extract" | "search" | "pinterest" | "pinjobs" | "videos" | "analytics" | "jobs" | "pinned" | "settings";

interface RecipeData {
  id: number;
  job_id: string;
  name: string;
  summary: string;
  seo_title: string;
  slug: string;
  pinterest_title: string;
  pinterest_description: string;
  article_html: string;
  recipe_json: string;
  category: string;
  created_at: string;
  original_image_url?: string;
  enhanced_image_url?: string;
  featured_image_r2?: string;
  uploaded?: number;
  post_url?: string;
  site_id?: string; // Which website this recipe belongs to
}

// Extended job type with candidates for card display
interface JobWithDetails extends Omit<JobSummary, 'payhipUrl'> {
  candidates?: Array<{ url: string; score: number }> | null;
  featuredImageR2?: string;
  textOverlayUrl?: string;
  postUrl?: string;
  pinterestScheduled?: boolean;
  payhipUrl?: string | null;
}

export function App() {
  const [activeTab, setActiveTab] = useState<TabType>("jobs");
  const [jobs, setJobs] = useState<JobWithDetails[]>([]);
  const [recipes, setRecipes] = useState<RecipeData[]>([]);
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [selectedRecipes, setSelectedRecipes] = useState<Set<number>>(new Set());
  const [selectedJob, setSelectedJob] = useState<JobResponse | null>(null);
  const [selectedRecipe, setSelectedRecipe] = useState<RecipeData | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUpdatingImage, setIsUpdatingImage] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isGeneratingOverlay, setIsGeneratingOverlay] = useState(false);
  const [dualImageModal, setDualImageModal] = useState<{
    recipeId: number;
    candidates: Array<{ url: string; score?: number }>;
    topImageUrl: string;
    bottomImageUrl: string;
    bannerStyle: string;
  } | null>(null);
  const [isSchedulingPin, setIsSchedulingPin] = useState(false);
  const [isPostingFacebook, setIsPostingFacebook] = useState(false);
  const [scheduleDate, setScheduleDate] = useState<string>("");
  const [customPinLink, setCustomPinLink] = useState<string>("");
  const [customPinSiteId, setCustomPinSiteId] = useState<string>("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [hoverImageUrl, setHoverImageUrl] = useState<string | null>(null);
  const [hoverImagePos, setHoverImagePos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  // Multi-site support
  const [sites, setSites] = useState<SiteConfig[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string>("cheftaling");

  // Site management state
  const [isAddingSite, setIsAddingSite] = useState(false);
  const [editingSite, setEditingSite] = useState<SiteConfig | null>(null);
  const [newSite, setNewSite] = useState({
    id: "",
    name: "",
    domain: "",
    wpBucket: "",
    cdnUrl: "",
    pinterestBrandName: "",
    pinterestBoards: [] as string[],
  });
  const [newBoardInput, setNewBoardInput] = useState("");
  const [editBoardInput, setEditBoardInput] = useState("");

  // Facebook accounts state
  interface FacebookAccount {
    id: number;
    name: string;
    userId: string | null;
    defaultGroupId: string | null;
    isActive: boolean;
    createdAt: string;
    lastUsedAt: string | null;
    hasCredentials: boolean;
    has2FA: boolean;
  }
  const [facebookAccounts, setFacebookAccounts] = useState<FacebookAccount[]>([]);
  const [isAddingFbAccount, setIsAddingFbAccount] = useState(false);
  const [selectedFbAccountId, setSelectedFbAccountId] = useState<number | null>(null);
  const [newFbAccount, setNewFbAccount] = useState({
    name: "",
    cookies: "",
    defaultGroupId: "",
    email: "",
    password: "",
    totpSecret: "",
  });

  // Payhip accounts state
  interface PayhipAccount {
    id: number;
    name: string;
    storeUrl: string | null;
    isActive: boolean;
    createdAt: string;
    lastUsedAt: string | null;
    hasCredentials?: boolean;
  }
  const [payhipAccounts, setPayhipAccounts] = useState<PayhipAccount[]>([]);
  const [isAddingPayhipAccount, setIsAddingPayhipAccount] = useState(false);
  const [editingPayhipAccountId, setEditingPayhipAccountId] = useState<number | null>(null);
  const [selectedPayhipAccountId, setSelectedPayhipAccountId] = useState<number | null>(null);
  const [isPostingPayhip, setIsPostingPayhip] = useState(false);
  const [newPayhipAccount, setNewPayhipAccount] = useState({
    name: "",
    cookies: "",
    storeUrl: "",
    email: "",
    password: "",
  });
  const [editPayhipAccount, setEditPayhipAccount] = useState({
    name: "",
    cookies: "",
    storeUrl: "",
    email: "",
    password: "",
  });

  // Metricool accounts state
  interface MetricoolAccountData {
    id: number;
    name: string;
    pinterest_brand_name: string;
    pinterest_boards: string[];
    custom_link: string;
    is_default: number;
    created_at: string;
    updated_at: string;
  }
  const [metricoolAccounts, setMetricoolAccounts] = useState<MetricoolAccountData[]>([]);
  const [isAddingMetricoolAccount, setIsAddingMetricoolAccount] = useState(false);
  const [editingMetricoolAccountId, setEditingMetricoolAccountId] = useState<number | null>(null);
  const [newMetricoolAccount, setNewMetricoolAccount] = useState({
    name: "",
    pinterest_brand_name: "",
    pinterest_boards: [] as string[],
    custom_link: "",
    is_default: false,
  });
  const [editMetricoolAccount, setEditMetricoolAccount] = useState({
    name: "",
    pinterest_brand_name: "",
    pinterest_boards: [] as string[],
    custom_link: "",
    is_default: false,
  });
  const [newMetricoolBoardInput, setNewMetricoolBoardInput] = useState("");
  const [editMetricoolBoardInput, setEditMetricoolBoardInput] = useState("");


  // Payhip local browser
  const [payhipLocalWs, setPayhipLocalWs] = useState<WebSocket | null>(null);
  const [payhipLocalStatus, setPayhipLocalStatus] = useState<string>("");
  const [payhipLocalConnected, setPayhipLocalConnected] = useState(false);
  const [showPayhipLocal, setShowPayhipLocal] = useState(false);

  const { job, isPolling, startPolling, stopPolling } = useJobPoller();

  // Check URL for pinterest_search param on mount and switch to Pinterest tab
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get("pinterest_search");
    if (searchParam) {
      setActiveTab("pinterest");
    }
  }, []);

  // Fetch available sites on mount
  useEffect(() => {
    async function fetchSites() {
      try {
        const response = await fetch(apiUrl("/api/sites"));
        const data = (await response.json()) as { sites?: SiteConfig[] };
        const sitesList = data.sites || [];
        setSites(sitesList);
        // Set default site if available
        const firstSite = sitesList[0];
        if (firstSite) {
          setSelectedSiteId(firstSite.id);
        }
      } catch (err) {
        console.error("Failed to fetch sites:", err);
      }
    }
    fetchSites();
  }, []);

  // Fetch Facebook accounts on mount
  useEffect(() => {
    async function fetchFacebookAccounts() {
      try {
        const response = await fetch(apiUrl("/api/facebook/accounts"));
        const data = (await response.json()) as { accounts?: FacebookAccount[] };
        setFacebookAccounts(data.accounts || []);
        // Set first account as default if available
        const firstAccount = data.accounts?.[0];
        if (firstAccount) {
          setSelectedFbAccountId(firstAccount.id);
        }
      } catch (err) {
        console.error("Failed to fetch Facebook accounts:", err);
      }
    }
    fetchFacebookAccounts();
  }, []);

  // Fetch Payhip accounts on mount
  useEffect(() => {
    async function fetchPayhipAccounts() {
      try {
        const response = await fetch(apiUrl("/api/payhip/accounts"));
        const data = (await response.json()) as { accounts?: PayhipAccount[] };
        setPayhipAccounts(data.accounts || []);
        // Set first account as default if available
        const firstAccount = data.accounts?.[0];
        if (firstAccount) {
          setSelectedPayhipAccountId(firstAccount.id);
        }
      } catch (err) {
        console.error("Failed to fetch Payhip accounts:", err);
      }
    }
    fetchPayhipAccounts();
  }, []);

  // Fetch Metricool accounts on mount
  useEffect(() => {
    async function fetchMetricoolAccounts() {
      try {
        const response = await fetch(apiUrl("/api/metricool/accounts"));
        const data = (await response.json()) as { accounts?: MetricoolAccountData[] };
        setMetricoolAccounts(data.accounts || []);
      } catch (err) {
        console.error("Failed to fetch Metricool accounts:", err);
      }
    }
    fetchMetricoolAccounts();
  }, []);

  // Site management functions
  async function handleAddSite() {
    if (!newSite.id || !newSite.name || !newSite.domain || !newSite.wpBucket || !newSite.cdnUrl || !newSite.pinterestBrandName) {
      alert("All fields are required");
      return;
    }

    try {
      const response = await fetch(apiUrl("/api/sites"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newSite),
      });
      const data = await response.json();

      if (data.success) {
        setSites([...sites, data.site]);
        setNewSite({ id: "", name: "", domain: "", wpBucket: "", cdnUrl: "", pinterestBrandName: "", pinterestBoards: [] });
        setNewBoardInput("");
        setIsAddingSite(false);
      } else {
        alert(data.error || "Failed to add site");
      }
    } catch (err) {
      console.error("Failed to add site:", err);
      alert("Failed to add site");
    }
  }

  function handleAddNewBoard() {
    if (!newBoardInput.trim()) return;
    if (newSite.pinterestBoards.includes(newBoardInput.trim())) return;
    setNewSite({ ...newSite, pinterestBoards: [...newSite.pinterestBoards, newBoardInput.trim()] });
    setNewBoardInput("");
  }

  function handleRemoveNewBoard(board: string) {
    setNewSite({ ...newSite, pinterestBoards: newSite.pinterestBoards.filter(b => b !== board) });
  }

  function handleAddEditBoard() {
    if (!editBoardInput.trim() || !editingSite) return;
    if (editingSite.pinterestBoards?.includes(editBoardInput.trim())) return;
    setEditingSite({ ...editingSite, pinterestBoards: [...(editingSite.pinterestBoards || []), editBoardInput.trim()] });
    setEditBoardInput("");
  }

  function handleRemoveEditBoard(board: string) {
    if (!editingSite) return;
    setEditingSite({ ...editingSite, pinterestBoards: (editingSite.pinterestBoards || []).filter(b => b !== board) });
  }

  // Metricool account board management
  function handleAddNewMetricoolBoard() {
    if (!newMetricoolBoardInput.trim()) return;
    if (newMetricoolAccount.pinterest_boards.includes(newMetricoolBoardInput.trim())) return;
    setNewMetricoolAccount({ ...newMetricoolAccount, pinterest_boards: [...newMetricoolAccount.pinterest_boards, newMetricoolBoardInput.trim()] });
    setNewMetricoolBoardInput("");
  }

  function handleRemoveNewMetricoolBoard(board: string) {
    setNewMetricoolAccount({ ...newMetricoolAccount, pinterest_boards: newMetricoolAccount.pinterest_boards.filter(b => b !== board) });
  }

  function handleAddEditMetricoolBoard() {
    if (!editMetricoolBoardInput.trim()) return;
    if (editMetricoolAccount.pinterest_boards.includes(editMetricoolBoardInput.trim())) return;
    setEditMetricoolAccount({ ...editMetricoolAccount, pinterest_boards: [...editMetricoolAccount.pinterest_boards, editMetricoolBoardInput.trim()] });
    setEditMetricoolBoardInput("");
  }

  function handleRemoveEditMetricoolBoard(board: string) {
    setEditMetricoolAccount({ ...editMetricoolAccount, pinterest_boards: editMetricoolAccount.pinterest_boards.filter(b => b !== board) });
  }

  async function handleUpdateSite() {
    if (!editingSite) return;

    try {
      const response = await fetch(apiUrl(`/api/sites/${editingSite.id}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editingSite),
      });
      const data = await response.json();

      if (data.success) {
        setSites(sites.map(s => s.id === editingSite.id ? data.site : s));
        setEditingSite(null);
      } else {
        alert(data.error || "Failed to update site");
      }
    } catch (err) {
      console.error("Failed to update site:", err);
      alert("Failed to update site");
    }
  }

  async function handleDeleteSite(siteId: string) {
    if (!confirm("Are you sure you want to delete this site?")) return;

    try {
      const response = await fetch(apiUrl(`/api/sites/${siteId}`), {
        method: "DELETE",
      });
      const data = await response.json();

      if (data.success) {
        setSites(sites.filter(s => s.id !== siteId));
        if (selectedSiteId === siteId && sites.length > 1) {
          const remaining = sites.filter(s => s.id !== siteId);
          if (remaining[0]) setSelectedSiteId(remaining[0].id);
        }
      } else {
        alert(data.error || "Failed to delete site");
      }
    } catch (err) {
      console.error("Failed to delete site:", err);
      alert("Failed to delete site");
    }
  }

  // Facebook account management functions
  async function handleAddFbAccount() {
    if (!newFbAccount.name) {
      alert("Name is required");
      return;
    }

    // Either cookies or email/password is required
    if (!newFbAccount.cookies && !newFbAccount.email) {
      alert("Either cookies or email/password is required");
      return;
    }

    try {
      const response = await fetch(apiUrl("/api/facebook/accounts"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newFbAccount),
      });
      const data = await response.json();

      if (data.success) {
        // Refetch accounts to get full data
        const accountsRes = await fetch(apiUrl("/api/facebook/accounts"));
        const accountsData = (await accountsRes.json()) as { accounts?: FacebookAccount[] };
        setFacebookAccounts(accountsData.accounts || []);
        setNewFbAccount({ name: "", cookies: "", defaultGroupId: "", email: "", password: "", totpSecret: "" });
        setIsAddingFbAccount(false);
      } else {
        alert(data.error || "Failed to add account");
      }
    } catch (err) {
      console.error("Failed to add Facebook account:", err);
      alert("Failed to add account");
    }
  }

  async function handleDeleteFbAccount(accountId: number) {
    if (!confirm("Are you sure you want to delete this Facebook account?")) return;

    try {
      const response = await fetch(apiUrl(`/api/facebook/accounts/${accountId}`), {
        method: "DELETE",
      });
      const data = await response.json();

      if (data.success) {
        setFacebookAccounts(facebookAccounts.filter(a => a.id !== accountId));
        if (selectedFbAccountId === accountId) {
          const remaining = facebookAccounts.filter(a => a.id !== accountId);
          const firstRemaining = remaining[0];
          setSelectedFbAccountId(firstRemaining ? firstRemaining.id : null);
        }
      } else {
        alert(data.error || "Failed to delete account");
      }
    } catch (err) {
      console.error("Failed to delete Facebook account:", err);
      alert("Failed to delete account");
    }
  }

  // Payhip account management functions
  async function handleAddPayhipAccount() {
    if (!newPayhipAccount.name) {
      alert("Name is required");
      return;
    }
    // Either email+password OR cookies are required
    if (!newPayhipAccount.email && !newPayhipAccount.cookies) {
      alert("Either email/password or cookies are required");
      return;
    }
    if (newPayhipAccount.email && !newPayhipAccount.password) {
      alert("Password is required when email is provided");
      return;
    }

    try {
      const response = await fetch(apiUrl("/api/payhip/accounts"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newPayhipAccount),
      });
      const data = await response.json();

      if (data.success) {
        const accountsRes = await fetch(apiUrl("/api/payhip/accounts"));
        const accountsData = (await accountsRes.json()) as { accounts?: PayhipAccount[] };
        setPayhipAccounts(accountsData.accounts || []);
        setNewPayhipAccount({ name: "", cookies: "", storeUrl: "", email: "", password: "" });
        setIsAddingPayhipAccount(false);
      } else {
        alert(data.error || "Failed to add account");
      }
    } catch (err) {
      console.error("Failed to add Payhip account:", err);
      alert("Failed to add account");
    }
  }

  async function handleDeletePayhipAccount(accountId: number) {
    if (!confirm("Are you sure you want to delete this Payhip account?")) return;

    try {
      const response = await fetch(apiUrl(`/api/payhip/accounts/${accountId}`), {
        method: "DELETE",
      });
      const data = await response.json();

      if (data.success) {
        setPayhipAccounts(payhipAccounts.filter(a => a.id !== accountId));
        if (selectedPayhipAccountId === accountId) {
          const remaining = payhipAccounts.filter(a => a.id !== accountId);
          const firstRemaining = remaining[0];
          setSelectedPayhipAccountId(firstRemaining ? firstRemaining.id : null);
        }
      } else {
        alert(data.error || "Failed to delete account");
      }
    } catch (err) {
      console.error("Failed to delete Payhip account:", err);
      alert("Failed to delete account");
    }
  }

  function startEditPayhipAccount(account: PayhipAccount) {
    setEditingPayhipAccountId(account.id);
    setEditPayhipAccount({
      name: account.name,
      cookies: "", // Don't show existing cookies for security
      storeUrl: account.storeUrl || "",
      email: "", // Don't show existing email/password for security
      password: "",
    });
  }

  async function handleUpdatePayhipAccount() {
    if (!editingPayhipAccountId) return;

    try {
      const updateData: { name?: string; cookies?: string; storeUrl?: string; email?: string; password?: string } = {};
      if (editPayhipAccount.name) updateData.name = editPayhipAccount.name;
      if (editPayhipAccount.cookies) updateData.cookies = editPayhipAccount.cookies;
      if (editPayhipAccount.storeUrl) updateData.storeUrl = editPayhipAccount.storeUrl;
      if (editPayhipAccount.email) updateData.email = editPayhipAccount.email;
      if (editPayhipAccount.password) updateData.password = editPayhipAccount.password;

      const response = await fetch(apiUrl(`/api/payhip/accounts/${editingPayhipAccountId}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updateData),
      });
      const data = await response.json();

      if (data.success) {
        // Refetch accounts
        const accountsRes = await fetch(apiUrl("/api/payhip/accounts"));
        const accountsData = (await accountsRes.json()) as { accounts?: PayhipAccount[] };
        setPayhipAccounts(accountsData.accounts || []);
        setEditingPayhipAccountId(null);
        setEditPayhipAccount({ name: "", cookies: "", storeUrl: "", email: "", password: "" });
        alert("Account updated successfully!");
      } else {
        alert(data.error || "Failed to update account");
      }
    } catch (err) {
      console.error("Failed to update Payhip account:", err);
      alert("Failed to update account");
    }
  }

  // Metricool account management functions
  async function handleAddMetricoolAccount() {
    if (!newMetricoolAccount.name || !newMetricoolAccount.pinterest_brand_name) {
      alert("Name and Pinterest Brand Name are required");
      return;
    }

    try {
      const response = await fetch(apiUrl("/api/metricool/accounts"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newMetricoolAccount),
      });
      const data = await response.json();

      if (data.success) {
        const accountsRes = await fetch(apiUrl("/api/metricool/accounts"));
        const accountsData = (await accountsRes.json()) as { accounts?: MetricoolAccountData[] };
        setMetricoolAccounts(accountsData.accounts || []);
        setNewMetricoolAccount({ name: "", pinterest_brand_name: "", pinterest_boards: [], custom_link: "", is_default: false });
        setNewMetricoolBoardInput("");
        setIsAddingMetricoolAccount(false);
      } else {
        alert(data.error || "Failed to add account");
      }
    } catch (err) {
      console.error("Failed to add Metricool account:", err);
      alert("Failed to add account");
    }
  }

  async function handleDeleteMetricoolAccount(accountId: number) {
    if (!confirm("Are you sure you want to delete this Metricool account?")) return;

    try {
      const response = await fetch(apiUrl(`/api/metricool/accounts/${accountId}`), {
        method: "DELETE",
      });
      const data = await response.json();

      if (data.success) {
        setMetricoolAccounts(metricoolAccounts.filter(a => a.id !== accountId));
      } else {
        alert(data.error || "Failed to delete account");
      }
    } catch (err) {
      console.error("Failed to delete Metricool account:", err);
      alert("Failed to delete account");
    }
  }

  function startEditMetricoolAccount(account: MetricoolAccountData) {
    setEditingMetricoolAccountId(account.id);
    setEditMetricoolAccount({
      name: account.name,
      pinterest_brand_name: account.pinterest_brand_name,
      pinterest_boards: account.pinterest_boards || [],
      custom_link: account.custom_link || "",
      is_default: account.is_default === 1,
    });
    setEditMetricoolBoardInput("");
  }

  async function handleUpdateMetricoolAccount() {
    if (!editingMetricoolAccountId) return;

    try {
      const response = await fetch(apiUrl(`/api/metricool/accounts/${editingMetricoolAccountId}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editMetricoolAccount),
      });
      const data = await response.json();

      if (data.success) {
        const accountsRes = await fetch(apiUrl("/api/metricool/accounts"));
        const accountsData = (await accountsRes.json()) as { accounts?: MetricoolAccountData[] };
        setMetricoolAccounts(accountsData.accounts || []);
        setEditingMetricoolAccountId(null);
        setEditMetricoolAccount({ name: "", pinterest_brand_name: "", pinterest_boards: [], custom_link: "", is_default: false });
        setEditMetricoolBoardInput("");
      } else {
        alert(data.error || "Failed to update account");
      }
    } catch (err) {
      console.error("Failed to update Metricool account:", err)
      alert("Failed to update account");
    }
  }

  // Connect to local Payhip browser server
  function connectPayhipLocal() {
    if (payhipLocalWs) {
      payhipLocalWs.close();
    }

    const ws = new WebSocket("ws://localhost:8765");

    ws.onopen = () => {
      setPayhipLocalConnected(true);
      setPayhipLocalStatus("Connected to local browser server");
      ws.send(JSON.stringify({ action: "ping" }));
    };

    ws.onclose = () => {
      setPayhipLocalConnected(false);
      setPayhipLocalStatus("Disconnected from local server");
      setPayhipLocalWs(null);
    };

    ws.onerror = () => {
      setPayhipLocalConnected(false);
      setPayhipLocalStatus("Failed to connect. Make sure the local server is running.");
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === "status" || data.type === "cloudflare" || data.type === "login_required" || data.type === "ready") {
        setPayhipLocalStatus(data.message);
      } else if (data.type === "error") {
        setPayhipLocalStatus(`Error: ${data.message}`);
        setIsPostingPayhip(false);
      } else if (data.type === "submit_result") {
        if (data.success) {
          setPayhipLocalStatus(`✓ Posted! URL: ${data.url}`);
          setRefreshKey((k) => k + 1);
        } else {
          setPayhipLocalStatus(`Submit failed: ${data.error}`);
        }
        setIsPostingPayhip(false);
      } else if (data.type === "closed") {
        setPayhipLocalStatus("Browser closed");
        setIsPostingPayhip(false);
      }
    };

    setPayhipLocalWs(ws);
  }

  // Post to Payhip using local browser
  async function handlePostToPayhipLocal(recipeId: number) {
    if (isPostingPayhip) return;

    // Get cookies for the selected account
    const account = payhipAccounts.find(a => a.id === selectedPayhipAccountId);
    if (!account) {
      alert("Please select a Payhip account first");
      return;
    }

    // Fetch account cookies from API
    let cookies: unknown[] = [];
    try {
      const res = await fetch(apiUrl(`/api/payhip/accounts/${account.id}/cookies`));
      const data = await res.json() as { cookies?: string };
      if (data.cookies) {
        cookies = JSON.parse(data.cookies);
      }
    } catch {
      alert("Failed to get account cookies");
      return;
    }

    setIsPostingPayhip(true);
    setShowPayhipLocal(true);
    setPayhipLocalStatus("Connecting to local browser server...");

    // Connect if not connected
    if (!payhipLocalWs || payhipLocalWs.readyState !== WebSocket.OPEN) {
      const ws = new WebSocket("ws://localhost:8765");

      ws.onopen = () => {
        setPayhipLocalConnected(true);
        setPayhipLocalStatus("Opening browser...");
        ws.send(JSON.stringify({
          action: "post",
          recipe_id: recipeId,
          cookies: cookies,
        }));
      };

      ws.onclose = () => {
        setPayhipLocalConnected(false);
        setPayhipLocalStatus("Disconnected");
        setPayhipLocalWs(null);
      };

      ws.onerror = () => {
        setPayhipLocalConnected(false);
        setPayhipLocalStatus("Failed to connect. Run: python scripts/payhip_server.py");
        setIsPostingPayhip(false);
      };

      ws.onmessage = async (event) => {
        const data = JSON.parse(event.data);
        if (data.message) {
          setPayhipLocalStatus(data.message);
        }
        if (data.type === "error") {
          setIsPostingPayhip(false);
        } else if (data.type === "cookies_updated" && data.cookies && selectedPayhipAccountId) {
          // Auto-save updated cookies to the account
          try {
            await fetch(apiUrl(`/api/payhip/accounts/${selectedPayhipAccountId}`), {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ cookies: JSON.stringify(data.cookies) }),
            });
            setPayhipLocalStatus(data.message + " (saved to account)");
          } catch {
            console.error("Failed to save cookies to account");
          }
        } else if (data.type === "submit_result") {
          if (data.success) {
            alert("Posted to Payhip!");
            setRefreshKey((k) => k + 1);
          }
          setIsPostingPayhip(false);
        } else if (data.type === "closed") {
          setIsPostingPayhip(false);
        }
      };

      setPayhipLocalWs(ws);
    } else {
      payhipLocalWs.send(JSON.stringify({
        action: "post",
        recipe_id: recipeId,
        cookies: cookies,
      }));
    }
  }

  // Submit form in local browser
  function submitPayhipLocal() {
    if (payhipLocalWs && payhipLocalWs.readyState === WebSocket.OPEN) {
      payhipLocalWs.send(JSON.stringify({ action: "submit" }));
      setPayhipLocalStatus("Submitting...");
    }
  }

  // Close local browser
  function closePayhipLocal() {
    if (payhipLocalWs && payhipLocalWs.readyState === WebSocket.OPEN) {
      payhipLocalWs.send(JSON.stringify({ action: "close" }));
    }
    setShowPayhipLocal(false);
    setIsPostingPayhip(false);
  }

  async function handlePostToPayhip(recipeId: number) {
    if (isPostingPayhip) return;
    setIsPostingPayhip(true);

    try {
      const response = await fetch(apiUrl(`/api/recipes/${recipeId}/post-payhip`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          account_id: selectedPayhipAccountId || undefined,
        }),
      });
      const data = await response.json() as { success: boolean; error?: string };

      if (data.success) {
        alert("Posted to Payhip blog!");
        setRefreshKey((k) => k + 1);
      } else {
        alert(`Payhip posting failed: ${data.error || "Unknown error"}`);
      }
    } catch (err) {
      console.error("Payhip posting failed:", err);
      alert("Payhip posting failed. Check console for details.");
    } finally {
      setIsPostingPayhip(false);
    }
  }

  async function handleBulkPostToPayhip() {
    if (isPostingPayhip) return;

    const recipesToPost = jobs
      .filter((j) => selectedJobs.has(j.jobId) && j.recipeId && j.postUrl && !(j as JobWithDetails & { payhipPosted?: boolean }).payhipPosted)
      .map((j) => ({ id: j.recipeId as number, name: j.recipeName }));

    if (recipesToPost.length === 0) {
      alert("No published recipes without Payhip posts selected");
      return;
    }

    const accountName = payhipAccounts.find(a => a.id === selectedPayhipAccountId)?.name || "default";
    if (!confirm(`Post ${recipesToPost.length} recipes to Payhip using account "${accountName}"?`)) return;

    setIsPostingPayhip(true);
    let posted = 0;
    let failed = 0;

    for (const recipe of recipesToPost) {
      try {
        const response = await fetch(apiUrl(`/api/recipes/${recipe.id}/post-payhip`), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            account_id: selectedPayhipAccountId || undefined,
          }),
        });
        const data = await response.json();
        if (data.success) {
          posted++;
          console.log(`Posted to Payhip: ${recipe.name}`);
        } else {
          failed++;
          console.error(`Payhip post failed for ${recipe.name}:`, data.error);
        }
        if (recipesToPost.indexOf(recipe) < recipesToPost.length - 1) {
          await new Promise((r) => setTimeout(r, 3000));
        }
      } catch (err) {
        failed++;
        console.error(`Payhip post failed for ${recipe.name}:`, err);
      }
    }

    alert(`Payhip posting complete!\nPosted: ${posted}\nFailed: ${failed}`);
    setSelectedJobs(new Set());
    setRefreshKey((k) => k + 1);
    setIsPostingPayhip(false);
  }

  // Fetch jobs
  useEffect(() => {
    fetchJobs();
  }, [refreshKey]);


  async function fetchJobs() {
    try {
      const response = await fetch(apiUrl("/api/jobs"));
      const data = (await response.json()) as JobListResponse;

      // Fetch details for each job to get candidates
      const jobsWithDetails = await Promise.all(
        data.jobs.map(async (j) => {
          if (j.status === "done") {
            try {
              const detailRes = await fetch(apiUrl(`/api/jobs/${j.jobId}`));
              const detail = await detailRes.json() as JobResponse;

              // Get recipe info for post_url, featured_image_r2, and text_overlay_url
              let postUrl: string | undefined;
              let featuredImageR2: string | undefined;
              let textOverlayUrl: string | undefined;
              let pinterestScheduled: boolean | undefined;
              let payhipPosted: boolean | undefined = j.payhipPosted;
              let payhipUrl: string | null | undefined = j.payhipUrl;
              if (j.recipeId) {
                const recipeRes = await fetch(apiUrl(`/api/recipes/${j.recipeId}`));
                const recipeData = await recipeRes.json();
                // API returns recipe directly, not wrapped in {recipe: ...}
                if (recipeData) {
                  postUrl = recipeData.post_url;
                  featuredImageR2 = recipeData.featured_image_r2;
                  textOverlayUrl = recipeData.text_overlay_url;
                  pinterestScheduled = recipeData.pinterest_scheduled === 1;
                  payhipPosted = recipeData.payhip_posted === 1;
                  payhipUrl = recipeData.payhip_url;
                }
              }

              return {
                ...j,
                candidates: detail.candidates,
                postUrl,
                featuredImageR2,
                textOverlayUrl,
                pinterestScheduled,
                payhipPosted,
                payhipUrl,
              };
            } catch {
              return j;
            }
          }
          return j;
        })
      );

      setJobs(jobsWithDetails);
    } catch (err) {
      console.error("Failed to fetch jobs:", err);
    }
  }

  async function fetchRecipes() {
    try {
      const response = await fetch(apiUrl("/api/recipes"));
      const data = (await response.json()) as { recipes: RecipeData[] };
      setRecipes(data.recipes || []);
    } catch (err) {
      console.error("Failed to fetch recipes:", err);
    }
  }

  const handleJobCreated = useCallback((jobId: string) => {
    startPolling(jobId);
    setRefreshKey((k) => k + 1);
  }, [startPolling]);

  const handleProcessVideos = useCallback(async (videos: import("@dishsnap/shared").SearchVideo[]) => {
    localStorage.removeItem("dishsnap_selected_videos");

    for (const video of videos) {
      try {
        const videoUrl = `https://www.tiktok.com/@${video.author.uniqueId}/video/${video.videoId}`;
        await fetch(apiUrl("/api/jobs"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url: videoUrl }),
        });
      } catch (err) {
        console.error("Failed to create job:", err);
      }
    }

    setRefreshKey((k) => k + 1);
    setActiveTab("jobs");
  }, []);

  // Update job in real-time
  useEffect(() => {
    if (job && (job.status === "done" || job.status === "failed")) {
      setRefreshKey((k) => k + 1);
    }
  }, [job?.status]);

  function toggleJobSelection(jobId: string) {
    setSelectedJobs((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  }

  function selectAllJobs() {
    // Select all completed jobs
    const completedJobs = jobs.filter((j) => j.status === "done");
    if (selectedJobs.size === completedJobs.length && completedJobs.length > 0) {
      setSelectedJobs(new Set());
    } else {
      setSelectedJobs(new Set(completedJobs.map((j) => j.jobId)));
    }
  }

  async function handleBulkGenerate() {
    if (selectedJobs.size === 0) return;
    setIsGenerating(true);

    try {
      const response = await fetch(apiUrl("/api/recipes/bulk"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          jobs: Array.from(selectedJobs).map((jobId) => ({ jobId })),
          siteId: selectedSiteId,
        }),
      });
      const data = await response.json();

      alert(`Generated ${data.successful} recipes!\nSkipped (already exist): ${data.skipped || 0}\nFailed: ${data.failed}`);

      setSelectedJobs(new Set());
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error("Bulk generate failed:", err);
      alert("Bulk generate failed - check console for details");
    } finally {
      setIsGenerating(false);
    }
  }

  async function handlePublishJobRecipe(recipeId: number, pinDate?: string) {
    if (isPublishing) return;
    setIsPublishing(true);
    try {
      const response = await fetch(apiUrl(`/api/recipes/${recipeId}/publish`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ scheduleDate: pinDate || scheduleDate || undefined }),
      });
      const data = await response.json();
      if (data.success) {
        setRefreshKey((k) => k + 1);
      } else {
        alert(`Publish failed: ${data.error || "Unknown error"}`);
      }
    } catch (err) {
      console.error("Publish failed:", err);
    } finally {
      setIsPublishing(false);
    }
  }

  async function handleBulkPublishJobs() {
    if (selectedJobs.size === 0) return;
    setIsPublishing(true);

    try {
      const recipeIds = jobs
        .filter((j) => selectedJobs.has(j.jobId) && j.recipeId)
        .map((j) => j.recipeId as number);

      const response = await fetch(apiUrl("/api/recipes/bulk-publish"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ recipeIds }),
      });
      const data = await response.json();

      alert(`Published ${data.successful} of ${data.total} recipes!`);
      setSelectedJobs(new Set());
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error("Bulk publish failed:", err);
      alert("Bulk publish failed - check console for details");
    } finally {
      setIsPublishing(false);
    }
  }

  async function handleBulkGenerateTextOverlays() {
    if (selectedJobs.size === 0) return;
    setIsGeneratingOverlay(true);

    try {
      // Get recipe IDs that have recipes but no text overlay
      const recipeIds = jobs
        .filter((j) => selectedJobs.has(j.jobId) && j.recipeId && !j.textOverlayUrl)
        .map((j) => j.recipeId as number);

      if (recipeIds.length === 0) {
        alert("No recipes without text overlay selected");
        setIsGeneratingOverlay(false);
        return;
      }

      const response = await fetch(apiUrl("/api/recipes/bulk-text-overlay"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ recipeIds }),
      });
      const data = await response.json();

      alert(`Generated text overlays for ${data.successful} of ${data.total} recipes with random colors!`);
      setSelectedJobs(new Set());
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error("Bulk text overlay generation failed:", err);
      alert("Bulk text overlay generation failed - check console for details");
    } finally {
      setIsGeneratingOverlay(false);
    }
  }

  async function handleSingleGenerate(jobId: string) {
    setIsGenerating(true);
    try {
      const response = await fetch(apiUrl("/api/recipes/generate"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jobId, siteId: selectedSiteId }),
      });
      const data = await response.json();
      if (data.success) {
        setRefreshKey((k) => k + 1);
      }
    } catch (err) {
      console.error("Generate failed:", err);
    } finally {
      setIsGenerating(false);
    }
  }

  const TEXT_OVERLAY_VARIATIONS = [
    { key: "dark", color: "#000000", label: "Dark" },
    { key: "white", color: "#ffffff", label: "White" },
    { key: "red", color: "#b42828", label: "Red" },
    { key: "orange", color: "#e67e22", label: "Orange" },
    { key: "green", color: "#27ae60", label: "Green" },
    { key: "blue", color: "#2980b9", label: "Blue" },
    { key: "purple", color: "#8e44ad", label: "Purple" },
    { key: "pink", color: "#e84393", label: "Pink" },
    { key: "teal", color: "#009688", label: "Teal" },
    { key: "gold", color: "#b7950b", label: "Gold" },
  ];

  async function handleGenerateTextOverlay(recipeId: number, variation: string = "dark") {
    if (isGeneratingOverlay) return;
    setIsGeneratingOverlay(true);
    try {
      const response = await fetch(apiUrl(`/api/recipes/${recipeId}/text-overlay`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ style: "pinterest", position: "bottom", variation }),
      });
      const data = await response.json();
      if (data.success) {
        setRefreshKey((k) => k + 1);
      } else {
        alert(`Text overlay failed: ${data.error || "Unknown error"}`);
      }
    } catch (err) {
      console.error("Text overlay failed:", err);
    } finally {
      setIsGeneratingOverlay(false);
    }
  }

  // Open dual image modal to select top and bottom images
  function openDualImageModal(recipeId: number, candidates: Array<{ url: string; score?: number }>) {
    if (candidates.length < 2) {
      alert("Need at least 2 images for dual image overlay");
      return;
    }
    setDualImageModal({
      recipeId,
      candidates,
      topImageUrl: candidates[0]!.url,
      bottomImageUrl: candidates[1]!.url,
      bannerStyle: "random",
    });
  }

  async function handleGenerateDualImageOverlay() {
    if (!dualImageModal || isGeneratingOverlay) return;
    setIsGeneratingOverlay(true);
    try {
      const response = await fetch(apiUrl(`/api/recipes/${dualImageModal.recipeId}/dual-image-overlay`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          top_image_url: dualImageModal.topImageUrl,
          bottom_image_url: dualImageModal.bottomImageUrl,
          variation: "white",
          banner_style: dualImageModal.bannerStyle,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setDualImageModal(null);
        setRefreshKey((k) => k + 1);
      } else {
        alert(`Dual image overlay failed: ${data.error || "Unknown error"}`);
      }
    } catch (err) {
      console.error("Dual image overlay failed:", err);
      alert("Dual image overlay failed");
    } finally {
      setIsGeneratingOverlay(false);
    }
  }

  async function handleSchedulePinterestPin(recipeId: number, pinDate?: string, pinCustomLink?: string) {
    if (isSchedulingPin) return;
    setIsSchedulingPin(true);
    try {
      const response = await fetch(apiUrl(`/api/recipes/${recipeId}/schedule-pin`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          schedule_date: pinDate || scheduleDate || undefined,
          days_offset: (pinDate || scheduleDate) ? undefined : 0, // Only use days_offset if no date specified
          custom_link: pinCustomLink || customPinLink || undefined,
        }),
      });
      const data = await response.json();
      if (data.success) {
        alert(`Pinterest pin scheduled for ${data.scheduled_datetime || "soon"}!`);
        setScheduleDate(""); // Clear the date
        setCustomPinLink(""); // Clear custom link
        setRefreshKey((k) => k + 1);
      } else {
        alert(`Pinterest scheduling failed: ${data.error || "Unknown error"}`);
      }
    } catch (err) {
      console.error("Pinterest scheduling failed:", err);
      alert("Pinterest scheduling failed. Check console for details.");
    } finally {
      setIsSchedulingPin(false);
    }
  }

  async function handlePostToFacebook(recipeId: number) {
    if (isPostingFacebook) return;
    setIsPostingFacebook(true);
    try {
      const response = await fetch(apiUrl(`/api/recipes/${recipeId}/post-facebook`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          account_id: selectedFbAccountId || undefined,
        }),
      });
      const data = await response.json();
      if (data.success) {
        alert("Posted to Facebook group!");
        setRefreshKey((k) => k + 1);
      } else {
        alert(`Facebook posting failed: ${data.error || "Unknown error"}`);
      }
    } catch (err) {
      console.error("Facebook posting failed:", err);
      alert("Facebook posting failed. Check console for details.");
    } finally {
      setIsPostingFacebook(false);
    }
  }

  async function handleBulkPostToFacebook() {
    if (isPostingFacebook) return;

    // Get recipes that are published (have post_url) and not already posted to FB
    const recipesToPost = jobs
      .filter((j) => selectedJobs.has(j.jobId) && j.recipeId && j.postUrl && !j.facebookPosted)
      .map((j) => ({ id: j.recipeId as number, name: j.recipeName }));

    if (recipesToPost.length === 0) {
      alert("No published recipes without Facebook posts selected");
      return;
    }

    const accountName = facebookAccounts.find(a => a.id === selectedFbAccountId)?.name || "default";
    if (!confirm(`Post ${recipesToPost.length} recipes to Facebook using account "${accountName}"?`)) return;

    setIsPostingFacebook(true);
    let posted = 0;
    let failed = 0;

    for (const recipe of recipesToPost) {
      try {
        const response = await fetch(apiUrl(`/api/recipes/${recipe.id}/post-facebook`), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            account_id: selectedFbAccountId || undefined,
          }),
        });
        const data = await response.json();
        if (data.success) {
          posted++;
          console.log(`Posted to FB: ${recipe.name}`);
        } else {
          failed++;
          console.error(`FB post failed for ${recipe.name}:`, data.error);
        }
        // Wait 5 seconds between posts to avoid rate limiting
        if (recipesToPost.indexOf(recipe) < recipesToPost.length - 1) {
          await new Promise((r) => setTimeout(r, 5000));
        }
      } catch (err) {
        failed++;
        console.error(`FB post failed for ${recipe.name}:`, err);
      }
    }

    alert(`Facebook posting complete!\nPosted: ${posted}\nFailed: ${failed}`);
    setSelectedJobs(new Set());
    setRefreshKey((k) => k + 1);
    setIsPostingFacebook(false);
  }

  async function handleBulkSchedulePins() {
    if (selectedJobs.size === 0) return;
    setIsSchedulingPin(true);

    try {
      // Get recipe IDs that are published but not yet pinned
      const recipeIds = jobs
        .filter((j) => selectedJobs.has(j.jobId) && j.recipeId && (j.postUrl || customPinLink) && j.textOverlayUrl && !j.pinterestScheduled)
        .map((j) => j.recipeId as number);

      if (recipeIds.length === 0) {
        alert("No published recipes without Pinterest pins selected");
        setIsSchedulingPin(false);
        return;
      }

      console.log("Bulk PIN - scheduleDate value:", scheduleDate, "recipeIds:", recipeIds);

      const response = await fetch(apiUrl("/api/recipes/bulk-schedule-pin"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          recipeIds,
          schedule_date: scheduleDate || undefined,
          custom_link: customPinLink || undefined,
          site_id: customPinSiteId || undefined,
        }),
      });
      const data = await response.json();

      console.log("Bulk PIN response:", data);

      alert(`Scheduled ${data.successful} Pinterest pins!\nDate sent: ${scheduleDate || 'today'}\nAlready scheduled: ${data.already_scheduled || 0}\nFailed: ${data.failed}`);
      setSelectedJobs(new Set());
      setScheduleDate("");
      setCustomPinLink("");
      setCustomPinSiteId("");
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error("Bulk PIN scheduling failed:", err);
      alert("Bulk PIN scheduling failed - check console for details");
    } finally {
      setIsSchedulingPin(false);
    }
  }

  async function handleDeleteJob(jobId: string) {
    if (!confirm("Delete this extraction?")) return;
    try {
      await fetch(apiUrl(`/api/jobs/${jobId}`), { method: "DELETE" });
      setSelectedJobs((prev) => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error("Delete failed:", err);
    }
  }

  async function handleBulkDeleteJobs() {
    if (selectedJobs.size === 0) return;
    if (!confirm(`Delete ${selectedJobs.size} selected jobs? This cannot be undone.`)) return;

    let deleted = 0;
    let failed = 0;

    for (const jobId of selectedJobs) {
      try {
        await fetch(apiUrl(`/api/jobs/${jobId}`), { method: "DELETE" });
        deleted++;
      } catch {
        failed++;
      }
    }

    alert(`Deleted ${deleted} jobs${failed > 0 ? `, ${failed} failed` : ""}`);
    setSelectedJobs(new Set());
    setRefreshKey((k) => k + 1);
  }

  async function handleDeleteRecipe(recipeId: number) {
    if (!confirm("Delete this recipe?")) return;
    try {
      await fetch(apiUrl(`/api/recipes/${recipeId}`), { method: "DELETE" });
      setSelectedRecipe(null);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error("Delete failed:", err);
    }
  }

  async function handlePublishRecipe(recipeId: number, pinDate?: string) {
    if (isPublishing) return;
    setIsPublishing(true);
    try {
      const response = await fetch(apiUrl(`/api/recipes/${recipeId}/publish`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ scheduleDate: pinDate || scheduleDate || undefined }),
      });
      const data = await response.json();
      if (data.success) {
        alert(data.already_uploaded ? "Recipe was already published!" : "Recipe published successfully!");
        setRefreshKey((k) => k + 1);
        if (selectedRecipe && data.post_url) {
          setSelectedRecipe({ ...selectedRecipe, uploaded: 1, post_url: data.post_url });
        }
      } else {
        alert(`Publish failed: ${data.error || "Unknown error"}`);
      }
    } catch (err) {
      console.error("Publish failed:", err);
      alert("Publish failed - check console for details");
    } finally {
      setIsPublishing(false);
    }
  }

  function toggleRecipeSelection(recipeId: number) {
    setSelectedRecipes((prev) => {
      const next = new Set(prev);
      if (next.has(recipeId)) next.delete(recipeId);
      else next.add(recipeId);
      return next;
    });
  }

  function selectAllUnpublishedRecipes() {
    const unpublished = recipes.filter((r) => !r.uploaded);
    if (selectedRecipes.size === unpublished.length && unpublished.length > 0) {
      setSelectedRecipes(new Set());
    } else {
      setSelectedRecipes(new Set(unpublished.map((r) => r.id)));
    }
  }

  async function handleBulkPublish() {
    if (selectedRecipes.size === 0) return;
    setIsPublishing(true);

    try {
      const response = await fetch(apiUrl("/api/recipes/bulk-publish"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          recipeIds: Array.from(selectedRecipes),
        }),
      });
      const data = await response.json();

      alert(`Published ${data.successful} of ${data.total} recipes!\n` +
            `Already published: ${data.already_uploaded || 0}\n` +
            `Failed: ${data.failed}`);

      setSelectedRecipes(new Set());
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error("Bulk publish failed:", err);
      alert("Bulk publish failed - check console for details");
    } finally {
      setIsPublishing(false);
    }
  }

  async function viewJobDetails(jobId: string) {
    try {
      const response = await fetch(apiUrl(`/api/jobs/${jobId}`));
      const data = (await response.json()) as JobResponse;
      setSelectedJob(data);
    } catch (err) {
      console.error("Failed to fetch job:", err);
    }
  }

  async function handleSelectImage(jobId: string, imageUrl: string) {
    if (isUpdatingImage) return;
    setIsUpdatingImage(true);
    try {
      const response = await fetch(apiUrl(`/api/jobs/${jobId}/image`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageUrl }),
      });
      if (response.ok) {
        if (selectedJob) {
          setSelectedJob({ ...selectedJob, imageUrl });
        }
        setRefreshKey((k) => k + 1);
      }
    } catch (err) {
      console.error("Failed to update image:", err);
    } finally {
      setIsUpdatingImage(false);
    }
  }

  const completedJobs = jobs.filter((j) => j.status === "done");
  const processingJobs = jobs.filter((j) => !["done", "failed"].includes(j.status));

  return (
    <div className={styles.dashboard}>
      {/* Top Navbar */}
      <header className={styles.topNav}>
        <div className={styles.logo}>
          <span className={styles.logoIcon}>🍽️</span>
          <span className={styles.logoText}>DishSnap</span>
        </div>

        <nav className={styles.navTabs}>
          <button
            className={`${styles.navTab} ${activeTab === "jobs" ? styles.active : ""}`}
            onClick={() => { setActiveTab("jobs"); fetchJobs(); }}
          >
            Jobs
            {processingJobs.length > 0 && (
              <span className={styles.badge}>{processingJobs.length}</span>
            )}
          </button>
          <button
            className={`${styles.navTab} ${activeTab === "extract" ? styles.active : ""}`}
            onClick={() => setActiveTab("extract")}
          >
            Extract
          </button>
          <button
            className={`${styles.navTab} ${activeTab === "search" ? styles.active : ""}`}
            onClick={() => setActiveTab("search")}
          >
            Search
          </button>
          <button
            className={`${styles.navTab} ${activeTab === "pinterest" ? styles.active : ""}`}
            onClick={() => setActiveTab("pinterest")}
          >
            Pinterest
          </button>
          <button
            className={`${styles.navTab} ${activeTab === "pinjobs" ? styles.active : ""}`}
            onClick={() => setActiveTab("pinjobs")}
          >
            Pin Jobs
          </button>
          <button
            className={`${styles.navTab} ${activeTab === "videos" ? styles.active : ""}`}
            onClick={() => setActiveTab("videos")}
          >
            Videos
          </button>
          <button
            className={`${styles.navTab} ${activeTab === "analytics" ? styles.active : ""}`}
            onClick={() => setActiveTab("analytics")}
          >
            Analytics
          </button>
          <button
            className={`${styles.navTab} ${activeTab === "pinned" ? styles.active : ""}`}
            onClick={() => { setActiveTab("pinned"); fetchJobs(); }}
          >
            Pinned
            {jobs.filter(j => j.pinterestScheduled).length > 0 && (
              <span className={styles.badgePink}>{jobs.filter(j => j.pinterestScheduled).length}</span>
            )}
          </button>
          <button
            className={`${styles.navTab} ${activeTab === "settings" ? styles.active : ""}`}
            onClick={() => setActiveTab("settings")}
          >
            Settings
          </button>
        </nav>

        <div className={styles.navRight}>
          <div className={styles.navStats}>
            <span>{completedJobs.length} extracted</span>
            <span>{recipes.length} recipes</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={styles.main}>
        {/* Jobs Tab - Card Layout */}
        {activeTab === "jobs" && (
          <div className={styles.jobsContainer}>
            <div className={styles.jobsHeader}>
              <div>
                <h1 className={styles.pageTitle}>Extractions</h1>
                <p className={styles.pageDesc}>
                  {jobs.length} total • {jobs.filter(j => j.recipeId).length} with recipes • {jobs.filter(j => j.recipeUploaded).length} published
                </p>
              </div>

              {selectedJobs.size > 0 && (
                <div className={styles.bulkActions}>
                  {/* Show selected site for recipe generation */}
                  {selectedJobs.size > 0 && jobs.filter(j => selectedJobs.has(j.jobId) && !j.recipeId).length > 0 && (
                    <div className={styles.bulkSiteInfo}>
                      <span>Generate for:</span>
                      <select
                        className={styles.bulkSiteSelect}
                        value={selectedSiteId}
                        onChange={(e) => setSelectedSiteId(e.target.value)}
                      >
                        {sites.map((site) => (
                          <option key={site.id} value={site.id}>{site.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <button
                    onClick={handleBulkGenerate}
                    className={styles.bulkBtn}
                    disabled={isGenerating || jobs.filter(j => selectedJobs.has(j.jobId) && !j.recipeId).length === 0}
                  >
                    {isGenerating ? "Generating..." : `Generate Recipe (${jobs.filter(j => selectedJobs.has(j.jobId) && !j.recipeId).length})`}
                  </button>
                  <button
                    onClick={handleBulkGenerateTextOverlays}
                    className={styles.bulkBtnPurple}
                    disabled={isGeneratingOverlay}
                  >
                    {isGeneratingOverlay ? "Generating..." : `TXT (${jobs.filter(j => selectedJobs.has(j.jobId) && j.recipeId && !j.textOverlayUrl).length})`}
                  </button>
                  <button
                    onClick={handleBulkPublishJobs}
                    className={styles.bulkBtnGreen}
                    disabled={isPublishing}
                  >
                    {isPublishing ? "Publishing..." : `Publish (${selectedJobs.size})`}
                  </button>
                  <div className={styles.bulkPinRow}>
                    <input
                      type="date"
                      className={styles.bulkScheduleDateInput}
                      value={scheduleDate}
                      onChange={(e) => setScheduleDate(e.target.value)}
                      placeholder="PIN date"
                    />
                    <input
                      type="text"
                      className={styles.bulkScheduleDateInput}
                      value={customPinLink}
                      onChange={(e) => setCustomPinLink(e.target.value)}
                      placeholder="Custom link (optional)"
                      style={{ width: "140px", fontSize: "11px" }}
                    />
                    {customPinLink && sites.length > 1 && (
                      <select
                        className={styles.bulkSiteSelect}
                        value={customPinSiteId}
                        onChange={(e) => setCustomPinSiteId(e.target.value)}
                        style={{ width: "100px", fontSize: "11px" }}
                      >
                        <option value="">Default Site</option>
                        {sites.map((site) => (
                          <option key={site.id} value={site.id}>{site.name}</option>
                        ))}
                      </select>
                    )}
                    <button
                      onClick={handleBulkSchedulePins}
                      className={styles.bulkBtnPink}
                      disabled={isSchedulingPin || jobs.filter(j => selectedJobs.has(j.jobId) && (j.postUrl || customPinLink) && j.textOverlayUrl && !j.pinterestScheduled).length === 0}
                    >
                      {isSchedulingPin ? "Pinning..." : `PIN (${jobs.filter(j => selectedJobs.has(j.jobId) && (j.postUrl || customPinLink) && j.textOverlayUrl && !j.pinterestScheduled).length})`}
                    </button>
                  </div>
                  <div className={styles.bulkPinRow}>
                    {facebookAccounts.length > 0 && (
                      <select
                        className={styles.bulkSiteSelect}
                        value={selectedFbAccountId || ""}
                        onChange={(e) => setSelectedFbAccountId(e.target.value ? Number(e.target.value) : null)}
                      >
                        <option value="">Default Account</option>
                        {facebookAccounts.map((acc) => (
                          <option key={acc.id} value={acc.id}>{acc.name}</option>
                        ))}
                      </select>
                    )}
                    <button
                      onClick={handleBulkPostToFacebook}
                      className={styles.bulkBtnBlue}
                      disabled={isPostingFacebook || jobs.filter(j => selectedJobs.has(j.jobId) && j.postUrl && !j.facebookPosted).length === 0}
                    >
                      {isPostingFacebook ? "Posting FB..." : `FB (${jobs.filter(j => selectedJobs.has(j.jobId) && j.postUrl && !j.facebookPosted).length})`}
                    </button>
                  </div>
                  <div className={styles.bulkPinRow}>
                    {payhipAccounts.length > 0 && (
                      <select
                        className={styles.bulkSiteSelect}
                        value={selectedPayhipAccountId || ""}
                        onChange={(e) => setSelectedPayhipAccountId(e.target.value ? Number(e.target.value) : null)}
                      >
                        <option value="">Default Account</option>
                        {payhipAccounts.map((acc) => (
                          <option key={acc.id} value={acc.id}>{acc.name}</option>
                        ))}
                      </select>
                    )}
                    <button
                      onClick={handleBulkPostToPayhip}
                      className={styles.bulkBtnOrange}
                      disabled={isPostingPayhip || jobs.filter(j => selectedJobs.has(j.jobId) && j.postUrl).length === 0}
                    >
                      {isPostingPayhip ? "Posting PH..." : `PH (${jobs.filter(j => selectedJobs.has(j.jobId) && j.postUrl).length})`}
                    </button>
                  </div>
                  <button
                    onClick={handleBulkDeleteJobs}
                    className={styles.bulkBtnDanger}
                  >
                    Delete ({selectedJobs.size})
                  </button>
                </div>
              )}
            </div>

            {/* Select All */}
            {jobs.filter(j => j.status === "done").length > 0 && (
              <div className={styles.selectAllBar}>
                <label className={styles.selectAllLabel}>
                  <input
                    type="checkbox"
                    checked={selectedJobs.size === jobs.filter(j => j.status === "done").length && selectedJobs.size > 0}
                    onChange={selectAllJobs}
                  />
                  Select all completed ({jobs.filter(j => j.status === "done").length})
                </label>
              </div>
            )}

            {/* Job Cards - one per row (exclude pinned jobs) */}
            <div className={styles.jobCards}>
              {jobs.filter(j => !j.pinterestScheduled).map((j) => (
                <div
                  key={j.jobId}
                  className={`${styles.jobCard} ${selectedJobs.has(j.jobId) ? styles.cardSelected : ""}`}
                >
                  {/* Selection Checkbox - show for all completed jobs */}
                  {j.status === "done" && (
                    <div className={styles.cardCheckbox}>
                      <input
                        type="checkbox"
                        checked={selectedJobs.has(j.jobId)}
                        onChange={() => toggleJobSelection(j.jobId)}
                      />
                    </div>
                  )}

                  {/* Images Section: 10 candidates in 5x2 grid + AI image */}
                  <div className={styles.cardImages}>
                    {j.candidates && j.candidates.length > 0 ? (
                      j.candidates.slice(0, 10).map((c, idx) => (
                        <div
                          key={idx}
                          className={`${styles.cardImageItem} ${j.imageUrl === c.url ? styles.selectedImage : ""}`}
                          onClick={() => handleSelectImage(j.jobId, c.url)}
                          onMouseEnter={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setHoverImageUrl(c.url);
                            setHoverImagePos({ x: rect.right + 10, y: rect.top });
                          }}
                          onMouseLeave={() => setHoverImageUrl(null)}
                        >
                          <img src={c.url} alt={`Frame ${idx + 1}`} />
                          <span className={styles.imageScore}>{Math.round(c.score)}%</span>
                        </div>
                      ))
                    ) : j.imageUrl ? (
                      <div
                        className={styles.cardImageSingle}
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setHoverImageUrl(j.imageUrl || null);
                          setHoverImagePos({ x: rect.right + 10, y: rect.top });
                        }}
                        onMouseLeave={() => setHoverImageUrl(null)}
                      >
                        <img src={j.imageUrl} alt="Extracted" />
                      </div>
                    ) : (
                      <div className={styles.cardImagePlaceholder}>
                        {j.status === "done" ? "✓" : j.status === "failed" ? "✗" : "⏳"}
                      </div>
                    )}

                    {/* AI Generated Image inline - show if featuredImageR2 exists OR recipeHasImage is true */}
                    {(j.featuredImageR2 || j.recipeHasImage) && (
                      <div
                        className={styles.cardAiImage}
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setHoverImageUrl(j.featuredImageR2 || j.imageUrl || null);
                          setHoverImagePos({ x: rect.right + 10, y: rect.top });
                        }}
                        onMouseLeave={() => setHoverImageUrl(null)}
                      >
                        <span className={styles.aiImageLabel}>AI</span>
                        <img src={j.featuredImageR2 || j.imageUrl || ""} alt="AI Enhanced" />
                      </div>
                    )}


                    {/* Text Overlay Image - show if exists */}
                    {j.textOverlayUrl && (
                      <div
                        className={styles.cardTextOverlay}
                        onMouseEnter={(e) => {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setHoverImageUrl(j.textOverlayUrl || null);
                          setHoverImagePos({ x: rect.right + 10, y: rect.top });
                        }}
                        onMouseLeave={() => setHoverImageUrl(null)}
                      >
                        <span className={styles.textOverlayLabel}>TXT</span>
                        <img src={j.textOverlayUrl} alt="With Text" />
                      </div>
                    )}

                    {/* Color picker for Text Overlay when no overlay yet */}
                    {j.recipeId && !j.textOverlayUrl && (
                      <div className={styles.cardTextOverlayPicker}>
                        <span className={styles.textPickerLabel}>TXT</span>
                        <div className={styles.colorDots}>
                          {TEXT_OVERLAY_VARIATIONS.map((v) => (
                            <button
                              key={v.key}
                              className={styles.colorDot}
                              style={{ backgroundColor: v.color, border: v.key === "white" ? "1px solid #ccc" : "none" }}
                              onClick={() => handleGenerateTextOverlay(j.recipeId!, v.key)}
                              disabled={isGeneratingOverlay}
                              title={v.label}
                            />
                          ))}
                          {/* Dual image button - shows when there are multiple candidates */}
                          {j.candidates && j.candidates.length >= 2 && (
                            <button
                              className={styles.dualImageBtn}
                              onClick={() => openDualImageModal(j.recipeId!, j.candidates!)}
                              disabled={isGeneratingOverlay}
                              title="Create dual image overlay (2 images + text banner)"
                            >
                              2️⃣
                            </button>
                          )}
                        </div>
                        {isGeneratingOverlay && <span className={styles.generatingText}>...</span>}
                      </div>
                    )}

                    {/* Regenerate text overlay with different color */}
                    {j.recipeId && j.textOverlayUrl && (
                      <div className={styles.cardTextOverlayRegenerate}>
                        <div className={styles.colorDotsSmall}>
                          {TEXT_OVERLAY_VARIATIONS.map((v) => (
                            <button
                              key={v.key}
                              className={styles.colorDotSmall}
                              style={{ backgroundColor: v.color, border: v.key === "white" ? "1px solid #ccc" : "none" }}
                              onClick={() => handleGenerateTextOverlay(j.recipeId!, v.key)}
                              disabled={isGeneratingOverlay}
                              title={`Regenerate with ${v.label}`}
                            />
                          ))}
                          {/* Dual image button for regeneration */}
                          {j.candidates && j.candidates.length >= 2 && (
                            <button
                              className={styles.dualImageBtnSmall}
                              onClick={() => openDualImageModal(j.recipeId!, j.candidates!)}
                              disabled={isGeneratingOverlay}
                              title="Create dual image overlay"
                            >
                              2️⃣
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Card Content */}
                  <div className={styles.cardContent}>
                    <div className={styles.cardContentRow}>
                      <h3 className={styles.cardTitle}>{j.videoTitle || "Untitled"}</h3>
                    </div>

                    {/* Recipe Info */}
                    {j.recipeName && (
                      <p className={styles.cardRecipe}>{j.recipeName}</p>
                    )}

                    {/* Status Pipeline */}
                    {j.status === "done" && (
                      <div className={styles.statusPipeline}>
                        <span className={`${styles.statusStep} ${j.recipeId ? styles.statusDone : styles.statusPending}`} title="Recipe Generated">
                          Recipe {j.recipeId ? "✓" : "○"}
                        </span>
                        <span className={`${styles.statusStep} ${j.textOverlayUrl ? styles.statusDone : j.recipeId ? styles.statusPending : styles.statusDisabled}`} title="Text Overlay">
                          TXT {j.textOverlayUrl ? "✓" : "○"}
                        </span>
                        <span className={`${styles.statusStep} ${j.recipeUploaded ? styles.statusDone : j.recipeId ? styles.statusPending : styles.statusDisabled}`} title="Published">
                          Pub {j.recipeUploaded ? "✓" : "○"}
                        </span>
                        <span className={`${styles.statusStep} ${j.pinterestScheduled ? styles.statusDone : j.postUrl ? styles.statusPending : styles.statusDisabled}`} title="Pinterest Scheduled">
                          PIN {j.pinterestScheduled ? "✓" : "○"}
                        </span>
                        <span className={`${styles.statusStep} ${j.payhipPosted ? styles.statusDone : j.postUrl ? styles.statusPending : styles.statusDisabled}`} title="Payhip Posted">
                          PH {j.payhipPosted ? "✓" : "○"}
                        </span>
                      </div>
                    )}

                    {/* Published URL and Pinterest */}
                    <div className={styles.cardLinksRow}>
                      {j.postUrl && (
                        <a
                          href={j.postUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.cardLink}
                          onClick={(e) => e.stopPropagation()}
                        >
                          View on Website →
                        </a>
                      )}

                      {/* Pinterest Pin Button - show if has text overlay (custom link can be used even without publishing) */}
                      {j.textOverlayUrl && j.recipeId && !j.pinterestScheduled && (
                        <div className={styles.pinRow}>
                          <input
                            type="date"
                            className={styles.scheduleDateInput}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setScheduleDate(e.target.value)}
                          />
                          <input
                            type="text"
                            placeholder="Custom link (optional)"
                            className={styles.scheduleDateInput}
                            style={{ width: "120px", fontSize: "10px" }}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setCustomPinLink(e.target.value)}
                            value={customPinLink}
                          />
                          <button
                            className={styles.pinButton}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSchedulePinterestPin(j.recipeId!);
                            }}
                            disabled={isSchedulingPin}
                          >
                            {isSchedulingPin ? "..." : "PIN"}
                          </button>
                        </div>
                      )}

                      {/* Pinterest Scheduled Badge */}
                      {j.pinterestScheduled && (
                        <span className={styles.pinnedBadge}>Pinned</span>
                      )}

                      {/* Payhip Posted Link */}
                      {j.payhipUrl && (
                        <a
                          href={j.payhipUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.cardLink}
                          onClick={(e) => e.stopPropagation()}
                          style={{ color: "#f97316" }}
                        >
                          View on Payhip →
                        </a>
                      )}

                      {/* Facebook Post Button - show after published */}
                      {j.postUrl && j.recipeId && (
                        <button
                          className={styles.fbButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePostToFacebook(j.recipeId!);
                          }}
                          disabled={isPostingFacebook}
                        >
                          {isPostingFacebook ? "..." : "FB"}
                        </button>
                      )}

                      {/* Payhip Post Button - show after published, hide if already posted */}
                      {j.postUrl && j.recipeId && !j.payhipPosted && (
                        <button
                          className={styles.phButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePostToPayhip(j.recipeId!);
                          }}
                          disabled={isPostingPayhip}
                          title="Post to Payhip (Cloud)"
                        >
                          {isPostingPayhip ? "..." : "PH"}
                        </button>
                      )}
                      {j.postUrl && j.recipeId && !j.payhipPosted && (
                        <button
                          className={styles.phButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePostToPayhipLocal(j.recipeId!);
                          }}
                          disabled={isPostingPayhip}
                          title="Post to Payhip (Local Browser - you can watch!)"
                          style={{ background: "#8b5cf6" }}
                        >
                          🖥️
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Card Actions */}
                  <div className={styles.cardActions}>
                    {j.status === "done" && !j.recipeId && (
                      <button
                        onClick={() => handleSingleGenerate(j.jobId)}
                        className={styles.cardBtn}
                        disabled={isGenerating}
                        title={`Generate recipe for ${sites.find(s => s.id === selectedSiteId)?.name || selectedSiteId}`}
                      >
                        {isGenerating ? "..." : `Recipe → ${sites.find(s => s.id === selectedSiteId)?.name || selectedSiteId}`}
                      </button>
                    )}

                    {j.recipeId && !j.recipeUploaded && (
                      <button
                        onClick={() => handlePublishJobRecipe(j.recipeId!)}
                        className={styles.cardBtnGreen}
                        disabled={isPublishing}
                      >
                        {isPublishing ? "..." : "Publish"}
                      </button>
                    )}

                    {j.recipeUploaded && (
                      <span className={styles.publishedTag}>Published</span>
                    )}

                    <button
                      onClick={() => handleDeleteJob(j.jobId)}
                      className={styles.cardBtnDanger}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}

              {jobs.length === 0 && (
                <div className={styles.emptyState}>
                  No extractions yet. Start by pasting a TikTok URL or searching for videos.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pinned Tab */}
        {activeTab === "pinned" && (
          <div className={styles.jobsContainer}>
            <div className={styles.jobsHeader}>
              <div>
                <h1 className={styles.pageTitle}>Pinned to Pinterest</h1>
                <p className={styles.pageDesc}>
                  Jobs that have been scheduled to Pinterest via Metricool
                </p>
              </div>
            </div>

            {/* Pinned Job Cards */}
            <div className={styles.jobCards}>
              {jobs.filter(j => j.pinterestScheduled).map((j) => (
                <div
                  key={j.jobId}
                  className={styles.jobCard}
                >
                  {/* Images Section */}
                  <div className={styles.cardImages}>
                    {j.textOverlayUrl ? (
                      <div className={styles.cardImageSingle}>
                        <img src={j.textOverlayUrl} alt="Pinterest Image" />
                      </div>
                    ) : j.imageUrl ? (
                      <div className={styles.cardImageSingle}>
                        <img src={j.imageUrl} alt="Extracted" />
                      </div>
                    ) : (
                      <div className={styles.cardImagePlaceholder}>✓</div>
                    )}
                  </div>

                  {/* Card Info */}
                  <div className={styles.cardInfo}>
                    <div className={styles.cardTitle}>{j.videoTitle || "Untitled"}</div>
                    <div className={styles.cardMeta}>
                      <span className={styles.pinnedBadge}>Pinned</span>
                      {j.postUrl && (
                        <a
                          href={j.postUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.cardLink}
                        >
                          View on Website →
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Card Actions */}
                  <div className={styles.cardActions}>
                    <button
                      onClick={() => handleDeleteJob(j.jobId)}
                      className={styles.cardBtnDanger}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}

              {jobs.filter(j => j.pinterestScheduled).length === 0 && (
                <div className={styles.emptyState}>
                  No pinned jobs yet. Schedule a Pinterest pin from the Jobs tab.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Extract Tab */}
        {activeTab === "extract" && (
          <div className={styles.panel}>
            <h2 className={styles.panelTitle}>Extract Dish Images</h2>
            <p className={styles.panelDesc}>Paste a TikTok recipe video URL to extract the best dish frame</p>

            <div className={styles.formWrapper}>
              <JobForm onJobCreated={handleJobCreated} disabled={isPolling} />
            </div>

            {isPolling && job && (
              <div className={styles.processingBox}>
                <JobStatus status={job.status} error={job.error} />
                <button onClick={stopPolling} className={styles.cancelBtn}>Cancel</button>
              </div>
            )}
          </div>
        )}

        {/* Search Tab */}
        {activeTab === "search" && (
          <div className={styles.panel}>
            <h2 className={styles.panelTitle}>Search TikTok Videos</h2>
            <p className={styles.panelDesc}>Find recipe videos and extract dishes in bulk</p>
            <SearchTab onProcessVideos={handleProcessVideos} disabled={false} />
          </div>
        )}

        {/* Pinterest Tab */}
        {activeTab === "pinterest" && (
          <div className={styles.panel}>
            <h2 className={styles.panelTitle}>Pinterest Search</h2>
            <p className={styles.panelDesc}>Discover recipe inspiration and pin ideas from Pinterest</p>
            <PinterestSearchTab
              disabled={false}
              onAddToPinJobs={() => setActiveTab("pinjobs")}
            />
          </div>
        )}

        {/* Pin Jobs Tab */}
        {activeTab === "pinjobs" && (
          <div className={styles.panel}>
            <PinJobsTab disabled={false} />
          </div>
        )}

        {/* Videos Tab */}
        {activeTab === "videos" && (
          <div className={styles.panel}>
            <VideosTab />
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" && (
          <div className={styles.panel}>
            <AnalyticsTab />
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <div className={styles.settingsContainer}>
            <div className={styles.settingsHeader}>
              <h1 className={styles.pageTitle}>Settings</h1>
              <p className={styles.pageDesc}>Manage your websites and configurations</p>
            </div>

            {/* Sites Management Section */}
            <div className={styles.settingsSection}>
              <div className={styles.sectionHeader}>
                <h2>Websites</h2>
                <button
                  className={styles.addSiteBtn}
                  onClick={() => setIsAddingSite(true)}
                >
                  + Add Website
                </button>
              </div>

              {/* Add Site Form */}
              {isAddingSite && (
                <div className={styles.siteForm}>
                  <h3>Add New Website</h3>
                  <div className={styles.formGrid}>
                    <div className={styles.formField}>
                      <label>Site ID</label>
                      <input
                        type="text"
                        placeholder="e.g., quickbites"
                        value={newSite.id}
                        onChange={(e) => setNewSite({ ...newSite, id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                      />
                      <span className={styles.fieldHint}>Lowercase, no spaces</span>
                    </div>
                    <div className={styles.formField}>
                      <label>Name</label>
                      <input
                        type="text"
                        placeholder="e.g., QuickBites"
                        value={newSite.name}
                        onChange={(e) => setNewSite({ ...newSite, name: e.target.value })}
                      />
                    </div>
                    <div className={styles.formField}>
                      <label>Domain</label>
                      <input
                        type="text"
                        placeholder="e.g., quickbites.com"
                        value={newSite.domain}
                        onChange={(e) => setNewSite({ ...newSite, domain: e.target.value })}
                      />
                    </div>
                    <div className={styles.formField}>
                      <label>R2 Bucket Name</label>
                      <input
                        type="text"
                        placeholder="e.g., quickbites"
                        value={newSite.wpBucket}
                        onChange={(e) => setNewSite({ ...newSite, wpBucket: e.target.value })}
                      />
                      <span className={styles.fieldHint}>WordPress CDN bucket</span>
                    </div>
                    <div className={styles.formField}>
                      <label>CDN URL</label>
                      <input
                        type="text"
                        placeholder="e.g., https://cdn.quickbites.com"
                        value={newSite.cdnUrl}
                        onChange={(e) => setNewSite({ ...newSite, cdnUrl: e.target.value })}
                      />
                    </div>
                    <div className={styles.formField}>
                      <label>Pinterest Brand Name</label>
                      <input
                        type="text"
                        placeholder="e.g., QuickBites"
                        value={newSite.pinterestBrandName}
                        onChange={(e) => setNewSite({ ...newSite, pinterestBrandName: e.target.value })}
                      />
                      <span className={styles.fieldHint}>Metricool brand name</span>
                    </div>
                  </div>

                  {/* Pinterest Boards */}
                  <div className={styles.boardsSection}>
                    <label>Pinterest Boards</label>
                    <div className={styles.boardsList}>
                      {newSite.pinterestBoards.map((board) => (
                        <span key={board} className={styles.boardTag}>
                          {board}
                          <button onClick={() => handleRemoveNewBoard(board)}>&times;</button>
                        </span>
                      ))}
                    </div>
                    <div className={styles.addBoardRow}>
                      <input
                        type="text"
                        placeholder="Add board name..."
                        value={newBoardInput}
                        onChange={(e) => setNewBoardInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddNewBoard())}
                      />
                      <button type="button" onClick={handleAddNewBoard}>Add</button>
                    </div>
                  </div>

                  <div className={styles.formActions}>
                    <button className={styles.cancelBtn} onClick={() => { setIsAddingSite(false); setNewSite({ id: "", name: "", domain: "", wpBucket: "", cdnUrl: "", pinterestBrandName: "", pinterestBoards: [] }); setNewBoardInput(""); }}>
                      Cancel
                    </button>
                    <button className={styles.saveBtn} onClick={handleAddSite}>
                      Add Website
                    </button>
                  </div>
                </div>
              )}

              {/* Edit Site Form */}
              {editingSite && (
                <div className={styles.siteForm}>
                  <h3>Edit Website: {editingSite.name}</h3>
                  <div className={styles.formGrid}>
                    <div className={styles.formField}>
                      <label>Site ID</label>
                      <input type="text" value={editingSite.id} disabled />
                      <span className={styles.fieldHint}>Cannot be changed</span>
                    </div>
                    <div className={styles.formField}>
                      <label>Name</label>
                      <input
                        type="text"
                        value={editingSite.name}
                        onChange={(e) => setEditingSite({ ...editingSite, name: e.target.value })}
                      />
                    </div>
                    <div className={styles.formField}>
                      <label>Domain</label>
                      <input
                        type="text"
                        value={editingSite.domain}
                        onChange={(e) => setEditingSite({ ...editingSite, domain: e.target.value })}
                      />
                    </div>
                    <div className={styles.formField}>
                      <label>R2 Bucket Name</label>
                      <input
                        type="text"
                        value={editingSite.wpBucket}
                        onChange={(e) => setEditingSite({ ...editingSite, wpBucket: e.target.value })}
                      />
                    </div>
                    <div className={styles.formField}>
                      <label>CDN URL</label>
                      <input
                        type="text"
                        value={editingSite.cdnUrl}
                        onChange={(e) => setEditingSite({ ...editingSite, cdnUrl: e.target.value })}
                      />
                    </div>
                    <div className={styles.formField}>
                      <label>Pinterest Brand Name</label>
                      <input
                        type="text"
                        value={editingSite.pinterestBrandName}
                        onChange={(e) => setEditingSite({ ...editingSite, pinterestBrandName: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Pinterest Boards */}
                  <div className={styles.boardsSection}>
                    <label>Pinterest Boards</label>
                    <div className={styles.boardsList}>
                      {(editingSite.pinterestBoards || []).map((board) => (
                        <span key={board} className={styles.boardTag}>
                          {board}
                          <button onClick={() => handleRemoveEditBoard(board)}>&times;</button>
                        </span>
                      ))}
                    </div>
                    <div className={styles.addBoardRow}>
                      <input
                        type="text"
                        placeholder="Add board name..."
                        value={editBoardInput}
                        onChange={(e) => setEditBoardInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddEditBoard())}
                      />
                      <button type="button" onClick={handleAddEditBoard}>Add</button>
                    </div>
                  </div>

                  <div className={styles.formActions}>
                    <button className={styles.cancelBtn} onClick={() => { setEditingSite(null); setEditBoardInput(""); }}>
                      Cancel
                    </button>
                    <button className={styles.saveBtn} onClick={handleUpdateSite}>
                      Save Changes
                    </button>
                  </div>
                </div>
              )}

              {/* Sites List */}
              <div className={styles.sitesList}>
                {sites.map((site) => (
                  <div key={site.id} className={styles.siteCard}>
                    <div className={styles.siteInfo}>
                      <h3>{site.name}</h3>
                      <p className={styles.siteDomain}>{site.domain}</p>
                      <div className={styles.siteDetails}>
                        <span>Bucket: {site.wpBucket}</span>
                        <span>Pinterest: {site.pinterestBrandName}</span>
                      </div>
                      {site.pinterestBoards && site.pinterestBoards.length > 0 && (
                        <div className={styles.siteBoardsPreview}>
                          Boards: {site.pinterestBoards.join(", ")}
                        </div>
                      )}
                    </div>
                    <div className={styles.siteActions}>
                      <button
                        className={styles.editSiteBtn}
                        onClick={() => setEditingSite(site)}
                      >
                        Edit
                      </button>
                      {!(site as SiteConfig & { isDefault?: boolean }).isDefault && (
                        <button
                          className={styles.deleteSiteBtn}
                          onClick={() => handleDeleteSite(site.id)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Facebook Accounts Section */}
            <div className={styles.settingsSection}>
              <div className={styles.sectionHeader}>
                <h2>Facebook Accounts</h2>
                <button
                  className={styles.addSiteBtn}
                  onClick={() => setIsAddingFbAccount(true)}
                >
                  + Add Account
                </button>
              </div>

              {/* Add Facebook Account Form */}
              {isAddingFbAccount && (
                <div className={styles.siteForm}>
                  <h3>Add Facebook Account</h3>
                  <div className={styles.formGrid}>
                    <div className={styles.formField}>
                      <label>Account Name</label>
                      <input
                        type="text"
                        placeholder="e.g., My Facebook"
                        value={newFbAccount.name}
                        onChange={(e) => setNewFbAccount({ ...newFbAccount, name: e.target.value })}
                      />
                      <span className={styles.fieldHint}>A friendly name for this account</span>
                    </div>
                    <div className={styles.formField}>
                      <label>Default Group ID (optional)</label>
                      <input
                        type="text"
                        placeholder="e.g., 123456789"
                        value={newFbAccount.defaultGroupId}
                        onChange={(e) => setNewFbAccount({ ...newFbAccount, defaultGroupId: e.target.value })}
                      />
                      <span className={styles.fieldHint}>Facebook group ID for posting</span>
                    </div>
                  </div>

                  <h4 style={{ marginTop: "1.5rem", marginBottom: "0.5rem", color: "#666" }}>Login Credentials (for auto-login)</h4>
                  <div className={styles.formGrid}>
                    <div className={styles.formField}>
                      <label>Email</label>
                      <input
                        type="email"
                        placeholder="facebook@email.com"
                        value={newFbAccount.email}
                        onChange={(e) => setNewFbAccount({ ...newFbAccount, email: e.target.value })}
                      />
                      <span className={styles.fieldHint}>Facebook login email</span>
                    </div>
                    <div className={styles.formField}>
                      <label>Password</label>
                      <input
                        type="password"
                        placeholder="********"
                        value={newFbAccount.password}
                        onChange={(e) => setNewFbAccount({ ...newFbAccount, password: e.target.value })}
                      />
                      <span className={styles.fieldHint}>Facebook login password</span>
                    </div>
                  </div>
                  <div className={styles.formField} style={{ marginTop: "0.5rem" }}>
                    <label>2FA TOTP Secret (optional)</label>
                    <input
                      type="text"
                      placeholder="e.g., JBSWY3DPEHPK3PXP"
                      value={newFbAccount.totpSecret}
                      onChange={(e) => setNewFbAccount({ ...newFbAccount, totpSecret: e.target.value })}
                      style={{ fontFamily: "monospace" }}
                    />
                    <span className={styles.fieldHint}>Base32 TOTP secret for 2FA (from authenticator app setup)</span>
                  </div>

                  <h4 style={{ marginTop: "1.5rem", marginBottom: "0.5rem", color: "#666" }}>Session Cookies (alternative to login)</h4>
                  <div className={styles.formField}>
                    <label>Cookies (JSON)</label>
                    <textarea
                      placeholder='Paste cookies JSON from browser extension...'
                      value={newFbAccount.cookies}
                      onChange={(e) => setNewFbAccount({ ...newFbAccount, cookies: e.target.value })}
                      style={{ width: "100%", minHeight: "100px", fontFamily: "monospace", fontSize: "12px" }}
                    />
                    <span className={styles.fieldHint}>Export cookies as JSON using a browser extension (e.g., Cookie-Editor)</span>
                  </div>
                  <div className={styles.formActions}>
                    <button className={styles.cancelBtn} onClick={() => { setIsAddingFbAccount(false); setNewFbAccount({ name: "", cookies: "", defaultGroupId: "", email: "", password: "", totpSecret: "" }); }}>
                      Cancel
                    </button>
                    <button className={styles.saveBtn} onClick={handleAddFbAccount}>
                      Add Account
                    </button>
                  </div>
                </div>
              )}

              {/* Facebook Accounts List */}
              <div className={styles.sitesList}>
                {facebookAccounts.map((account) => (
                  <div key={account.id} className={`${styles.siteCard} ${selectedFbAccountId === account.id ? styles.cardSelected : ""}`}>
                    <div className={styles.siteInfo}>
                      <h3>{account.name}</h3>
                      <div className={styles.siteDetails}>
                        <span>User ID: {account.userId || "Unknown"}</span>
                        <span>Status: {account.isActive ? "Active" : "Inactive"}</span>
                        {account.hasCredentials && <span style={{ color: "#22c55e" }}>Has Login</span>}
                        {account.has2FA && <span style={{ color: "#3b82f6" }}>2FA</span>}
                      </div>
                      {account.defaultGroupId && (
                        <div className={styles.siteBoardsPreview}>
                          Default Group: {account.defaultGroupId}
                        </div>
                      )}
                      {account.lastUsedAt && (
                        <div className={styles.siteBoardsPreview}>
                          Last used: {new Date(account.lastUsedAt).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <div className={styles.siteActions}>
                      <button
                        className={selectedFbAccountId === account.id ? styles.saveBtn : styles.editSiteBtn}
                        onClick={() => setSelectedFbAccountId(account.id)}
                      >
                        {selectedFbAccountId === account.id ? "Selected" : "Select"}
                      </button>
                      <button
                        className={styles.deleteSiteBtn}
                        onClick={() => handleDeleteFbAccount(account.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
                {facebookAccounts.length === 0 && (
                  <div className={styles.emptyState}>
                    No Facebook accounts configured. Add one to enable multi-account posting.
                  </div>
                )}
              </div>
            </div>

            {/* Payhip Accounts Section */}
            <div className={styles.settingsSection}>
              <div className={styles.sectionHeader}>
                <h2>Payhip Accounts</h2>
                <button
                  className={styles.addSiteBtn}
                  onClick={() => setIsAddingPayhipAccount(true)}
                >
                  + Add Account
                </button>
              </div>

              {/* Add Payhip Account Form */}
              {isAddingPayhipAccount && (
                <div className={styles.siteForm}>
                  <h3>Add Payhip Account</h3>
                  <div className={styles.formGrid}>
                    <div className={styles.formField}>
                      <label>Account Name</label>
                      <input
                        type="text"
                        placeholder="e.g., My Payhip Store"
                        value={newPayhipAccount.name}
                        onChange={(e) => setNewPayhipAccount({ ...newPayhipAccount, name: e.target.value })}
                      />
                      <span className={styles.fieldHint}>A friendly name for this account</span>
                    </div>
                    <div className={styles.formField}>
                      <label>Store URL (optional)</label>
                      <input
                        type="text"
                        placeholder="e.g., https://payhip.com/mystore"
                        value={newPayhipAccount.storeUrl}
                        onChange={(e) => setNewPayhipAccount({ ...newPayhipAccount, storeUrl: e.target.value })}
                      />
                      <span className={styles.fieldHint}>Your Payhip store URL</span>
                    </div>
                  </div>
                  <div className={styles.formGrid} style={{ marginTop: "1rem" }}>
                    <div className={styles.formField}>
                      <label>Payhip Email</label>
                      <input
                        type="email"
                        placeholder="your@email.com"
                        value={newPayhipAccount.email}
                        onChange={(e) => setNewPayhipAccount({ ...newPayhipAccount, email: e.target.value })}
                      />
                      <span className={styles.fieldHint}>Your Payhip login email</span>
                    </div>
                    <div className={styles.formField}>
                      <label>Payhip Password</label>
                      <input
                        type="password"
                        placeholder="Your password"
                        value={newPayhipAccount.password}
                        onChange={(e) => setNewPayhipAccount({ ...newPayhipAccount, password: e.target.value })}
                      />
                      <span className={styles.fieldHint}>Your Payhip login password</span>
                    </div>
                  </div>
                  <div className={styles.formField} style={{ marginTop: "1rem" }}>
                    <label>Cookies (JSON) - Optional if email/password provided</label>
                    <textarea
                      placeholder='Paste cookies JSON from browser extension (optional)...'
                      value={newPayhipAccount.cookies}
                      onChange={(e) => setNewPayhipAccount({ ...newPayhipAccount, cookies: e.target.value })}
                      style={{ width: "100%", minHeight: "80px", fontFamily: "monospace", fontSize: "12px" }}
                    />
                    <span className={styles.fieldHint}>Optional: Export cookies from payhip.com using Cookie-Editor</span>
                  </div>
                  <div className={styles.formActions}>
                    <button className={styles.cancelBtn} onClick={() => { setIsAddingPayhipAccount(false); setNewPayhipAccount({ name: "", cookies: "", storeUrl: "", email: "", password: "" }); }}>
                      Cancel
                    </button>
                    <button className={styles.saveBtn} onClick={handleAddPayhipAccount}>
                      Add Account
                    </button>
                  </div>
                </div>
              )}

              {/* Payhip Accounts List */}
              <div className={styles.sitesList}>
                {payhipAccounts.map((account) => (
                  <div key={account.id} className={`${styles.siteCard} ${selectedPayhipAccountId === account.id ? styles.cardSelected : ""}`}>
                    {editingPayhipAccountId === account.id ? (
                      // Edit form
                      <div className={styles.siteForm} style={{ margin: 0, border: "none" }}>
                        <h3>Edit Payhip Account</h3>
                        <div className={styles.formField}>
                          <label>Account Name</label>
                          <input
                            type="text"
                            value={editPayhipAccount.name}
                            onChange={(e) => setEditPayhipAccount({ ...editPayhipAccount, name: e.target.value })}
                          />
                        </div>
                        <div className={styles.formField}>
                          <label>Store URL (optional)</label>
                          <input
                            type="text"
                            value={editPayhipAccount.storeUrl}
                            onChange={(e) => setEditPayhipAccount({ ...editPayhipAccount, storeUrl: e.target.value })}
                          />
                        </div>
                        <div className={styles.formGrid} style={{ marginTop: "0.5rem" }}>
                          <div className={styles.formField}>
                            <label>Payhip Email</label>
                            <input
                              type="email"
                              placeholder="Update email..."
                              value={editPayhipAccount.email}
                              onChange={(e) => setEditPayhipAccount({ ...editPayhipAccount, email: e.target.value })}
                            />
                          </div>
                          <div className={styles.formField}>
                            <label>Payhip Password</label>
                            <input
                              type="password"
                              placeholder="Update password..."
                              value={editPayhipAccount.password}
                              onChange={(e) => setEditPayhipAccount({ ...editPayhipAccount, password: e.target.value })}
                            />
                          </div>
                        </div>
                        <div className={styles.formField}>
                          <label>New Cookies (optional)</label>
                          <textarea
                            placeholder="Paste new cookies JSON to update..."
                            value={editPayhipAccount.cookies}
                            onChange={(e) => setEditPayhipAccount({ ...editPayhipAccount, cookies: e.target.value })}
                            style={{ width: "100%", minHeight: "60px", fontFamily: "monospace", fontSize: "12px" }}
                          />
                          <span className={styles.fieldHint}>Leave empty to keep existing</span>
                        </div>
                        <div className={styles.formActions}>
                          <button className={styles.cancelBtn} onClick={() => setEditingPayhipAccountId(null)}>
                            Cancel
                          </button>
                          <button className={styles.saveBtn} onClick={handleUpdatePayhipAccount}>
                            Save Changes
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Display card
                      <>
                        <div className={styles.siteInfo}>
                          <h3>{account.name}</h3>
                          <div className={styles.siteDetails}>
                            <span>Status: {account.isActive ? "Active" : "Inactive"}</span>
                          </div>
                          {account.storeUrl && (
                            <div className={styles.siteBoardsPreview}>
                              Store: {account.storeUrl}
                            </div>
                          )}
                          {account.lastUsedAt && (
                            <div className={styles.siteBoardsPreview}>
                              Last used: {new Date(account.lastUsedAt).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                        <div className={styles.siteActions}>
                          <button
                            className={selectedPayhipAccountId === account.id ? styles.saveBtn : styles.editSiteBtn}
                            onClick={() => setSelectedPayhipAccountId(account.id)}
                          >
                            {selectedPayhipAccountId === account.id ? "Selected" : "Select"}
                          </button>
                          <button
                            className={styles.editSiteBtn}
                            onClick={() => startEditPayhipAccount(account)}
                          >
                            Edit
                          </button>
                          <button
                            className={styles.deleteSiteBtn}
                            onClick={() => handleDeletePayhipAccount(account.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
                {payhipAccounts.length === 0 && (
                  <div className={styles.emptyState}>
                    No Payhip accounts configured. Add one to enable blog posting.
                  </div>
                )}
              </div>
            </div>

            {/* Metricool Accounts Section */}
            <div className={styles.settingsSection}>
              <div className={styles.sectionHeader}>
                <h2>Metricool Accounts</h2>
                <button
                  className={styles.addSiteBtn}
                  onClick={() => setIsAddingMetricoolAccount(true)}
                >
                  + Add Account
                </button>
              </div>

              {/* Add Metricool Account Form */}
              {isAddingMetricoolAccount && (
                <div className={styles.siteForm}>
                  <h3>Add Metricool Account</h3>
                  <p style={{ fontSize: "0.85rem", color: "#666", marginBottom: "1rem" }}>
                    Use this for standalone Metricool/Pinterest accounts when you don't need a full website setup.
                  </p>
                  <div className={styles.formGrid}>
                    <div className={styles.formField}>
                      <label>Account Name</label>
                      <input
                        type="text"
                        placeholder="e.g., My Pinterest Account"
                        value={newMetricoolAccount.name}
                        onChange={(e) => setNewMetricoolAccount({ ...newMetricoolAccount, name: e.target.value })}
                      />
                      <span className={styles.fieldHint}>A friendly name for this account</span>
                    </div>
                    <div className={styles.formField}>
                      <label>Pinterest Brand Name</label>
                      <input
                        type="text"
                        placeholder="e.g., MyBrand"
                        value={newMetricoolAccount.pinterest_brand_name}
                        onChange={(e) => setNewMetricoolAccount({ ...newMetricoolAccount, pinterest_brand_name: e.target.value })}
                      />
                      <span className={styles.fieldHint}>Metricool Pinterest brand name</span>
                    </div>
                  </div>

                  {/* Custom Link */}
                  <div className={styles.formField}>
                    <label>Custom Link (Default)</label>
                    <input
                      type="url"
                      placeholder="https://example.com"
                      value={newMetricoolAccount.custom_link}
                      onChange={(e) => setNewMetricoolAccount({ ...newMetricoolAccount, custom_link: e.target.value })}
                    />
                    <span className={styles.fieldHint}>Default link URL for Pinterest pins</span>
                  </div>

                  {/* Pinterest Boards */}
                  <div className={styles.boardsSection}>
                    <label>Pinterest Boards</label>
                    <div className={styles.boardsList}>
                      {newMetricoolAccount.pinterest_boards.map((board) => (
                        <span key={board} className={styles.boardTag}>
                          {board}
                          <button onClick={() => handleRemoveNewMetricoolBoard(board)}>&times;</button>
                        </span>
                      ))}
                    </div>
                    <div className={styles.addBoardRow}>
                      <input
                        type="text"
                        placeholder="Add board name..."
                        value={newMetricoolBoardInput}
                        onChange={(e) => setNewMetricoolBoardInput(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddNewMetricoolBoard())}
                      />
                      <button type="button" onClick={handleAddNewMetricoolBoard}>Add</button>
                    </div>
                  </div>

                  <div className={styles.formField} style={{ marginTop: "1rem" }}>
                    <label>
                      <input
                        type="checkbox"
                        checked={newMetricoolAccount.is_default}
                        onChange={(e) => setNewMetricoolAccount({ ...newMetricoolAccount, is_default: e.target.checked })}
                        style={{ marginRight: "0.5rem" }}
                      />
                      Set as default account
                    </label>
                  </div>

                  <div className={styles.formActions}>
                    <button className={styles.cancelBtn} onClick={() => { setIsAddingMetricoolAccount(false); setNewMetricoolAccount({ name: "", pinterest_brand_name: "", pinterest_boards: [], custom_link: "", is_default: false }); setNewMetricoolBoardInput(""); }}>
                      Cancel
                    </button>
                    <button className={styles.saveBtn} onClick={handleAddMetricoolAccount}>
                      Add Account
                    </button>
                  </div>
                </div>
              )}

              {/* Metricool Accounts List */}
              <div className={styles.sitesList}>
                {metricoolAccounts.map((account) => (
                  <div key={account.id} className={styles.siteCard}>
                    {editingMetricoolAccountId === account.id ? (
                      // Edit form
                      <div className={styles.siteForm} style={{ margin: 0, border: "none" }}>
                        <h3>Edit Metricool Account</h3>
                        <div className={styles.formGrid}>
                          <div className={styles.formField}>
                            <label>Account Name</label>
                            <input
                              type="text"
                              value={editMetricoolAccount.name}
                              onChange={(e) => setEditMetricoolAccount({ ...editMetricoolAccount, name: e.target.value })}
                            />
                          </div>
                          <div className={styles.formField}>
                            <label>Pinterest Brand Name</label>
                            <input
                              type="text"
                              value={editMetricoolAccount.pinterest_brand_name}
                              onChange={(e) => setEditMetricoolAccount({ ...editMetricoolAccount, pinterest_brand_name: e.target.value })}
                            />
                          </div>
                        </div>

                        {/* Custom Link */}
                        <div className={styles.formField}>
                          <label>Custom Link (Default)</label>
                          <input
                            type="url"
                            placeholder="https://example.com"
                            value={editMetricoolAccount.custom_link}
                            onChange={(e) => setEditMetricoolAccount({ ...editMetricoolAccount, custom_link: e.target.value })}
                          />
                          <span className={styles.fieldHint}>Default link URL for Pinterest pins</span>
                        </div>

                        {/* Pinterest Boards */}
                        <div className={styles.boardsSection}>
                          <label>Pinterest Boards</label>
                          <div className={styles.boardsList}>
                            {editMetricoolAccount.pinterest_boards.map((board) => (
                              <span key={board} className={styles.boardTag}>
                                {board}
                                <button onClick={() => handleRemoveEditMetricoolBoard(board)}>&times;</button>
                              </span>
                            ))}
                          </div>
                          <div className={styles.addBoardRow}>
                            <input
                              type="text"
                              placeholder="Add board name..."
                              value={editMetricoolBoardInput}
                              onChange={(e) => setEditMetricoolBoardInput(e.target.value)}
                              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddEditMetricoolBoard())}
                            />
                            <button type="button" onClick={handleAddEditMetricoolBoard}>Add</button>
                          </div>
                        </div>

                        <div className={styles.formField} style={{ marginTop: "1rem" }}>
                          <label>
                            <input
                              type="checkbox"
                              checked={editMetricoolAccount.is_default}
                              onChange={(e) => setEditMetricoolAccount({ ...editMetricoolAccount, is_default: e.target.checked })}
                              style={{ marginRight: "0.5rem" }}
                            />
                            Set as default account
                          </label>
                        </div>

                        <div className={styles.formActions}>
                          <button className={styles.cancelBtn} onClick={() => setEditingMetricoolAccountId(null)}>
                            Cancel
                          </button>
                          <button className={styles.saveBtn} onClick={handleUpdateMetricoolAccount}>
                            Save Changes
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Display card
                      <>
                        <div className={styles.siteInfo}>
                          <h3>
                            {account.name}
                            {account.is_default === 1 && (
                              <span style={{ marginLeft: "0.5rem", fontSize: "0.7rem", background: "#e60023", color: "white", padding: "2px 6px", borderRadius: "4px" }}>
                                Default
                              </span>
                            )}
                          </h3>
                          <div className={styles.siteDetails}>
                            <span>Pinterest: {account.pinterest_brand_name}</span>
                          </div>
                          {account.custom_link && (
                            <div className={styles.siteDetails} style={{ fontSize: "0.8rem", color: "#666" }}>
                              Link: {account.custom_link}
                            </div>
                          )}
                          {account.pinterest_boards && account.pinterest_boards.length > 0 && (
                            <div className={styles.siteBoardsPreview}>
                              Boards: {account.pinterest_boards.join(", ")}
                            </div>
                          )}
                        </div>
                        <div className={styles.siteActions}>
                          <button
                            className={styles.editSiteBtn}
                            onClick={() => startEditMetricoolAccount(account)}
                          >
                            Edit
                          </button>
                          <button
                            className={styles.deleteSiteBtn}
                            onClick={() => handleDeleteMetricoolAccount(account.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
                {metricoolAccounts.length === 0 && (
                  <div className={styles.emptyState}>
                    No Metricool accounts configured. Add one to use with custom links in Pin Jobs.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Detail Panel */}
      {(selectedJob || selectedRecipe) && (
        <aside className={styles.detailPanel}>
          <button
            className={styles.closeBtn}
            onClick={() => { setSelectedJob(null); setSelectedRecipe(null); }}
          >
            ✕
          </button>

          {selectedJob && (
            <div className={styles.detailContent}>
              <h3>{selectedJob.videoTitle || "Untitled"}</h3>
              {selectedJob.author && <p className={styles.detailMeta}>by {selectedJob.author}</p>}

              {selectedJob.imageUrl && (
                <div className={styles.mainImageSection}>
                  <label className={styles.sectionLabel}>Selected Image</label>
                  <img src={selectedJob.imageUrl} alt="Selected" className={styles.mainImage} />
                </div>
              )}

              {selectedJob.candidates && selectedJob.candidates.length > 0 && (
                <div className={styles.candidatesSection}>
                  <label className={styles.sectionLabel}>
                    All Candidates ({selectedJob.candidates.length}) - Click to select
                  </label>
                  <div className={styles.candidatesGrid}>
                    {selectedJob.candidates.slice(0, 4).map((candidate, index) => {
                      const isSelected = selectedJob.imageUrl === candidate.url;
                      return (
                        <div
                          key={index}
                          className={`${styles.candidateItem} ${isSelected ? styles.candidateSelected : ""}`}
                          onClick={() => !isSelected && handleSelectImage(selectedJob.jobId, candidate.url)}
                        >
                          <img src={candidate.url} alt={`Candidate ${index + 1}`} />
                          <div className={styles.candidateInfo}>
                            <span className={styles.candidateScore}>
                              {Math.round(candidate.score)}%
                            </span>
                            {isSelected && (
                              <span className={styles.selectedBadge}>Selected</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {isUpdatingImage && (
                    <div className={styles.updatingOverlay}>Updating...</div>
                  )}
                </div>
              )}

              <div className={styles.detailActions}>
                <button
                  onClick={() => handleSingleGenerate(selectedJob.jobId)}
                  className={styles.generateBtn}
                  disabled={isGenerating}
                >
                  {isGenerating ? "Generating..." : "Generate Recipe"}
                </button>
              </div>
            </div>
          )}

          {selectedRecipe && (
            <div className={styles.detailContent}>
              <div className={styles.detailHeader}>
                <h3>{selectedRecipe.name}</h3>
                <span className={styles.categoryBadge}>{selectedRecipe.category}</span>
              </div>

              {selectedRecipe.uploaded ? (
                <div className={styles.publishedBanner}>
                  Published
                  {selectedRecipe.post_url && (
                    <a href={selectedRecipe.post_url} target="_blank" rel="noopener noreferrer">
                      View Post
                    </a>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => handlePublishRecipe(selectedRecipe.id)}
                  className={styles.publishBtn}
                  disabled={isPublishing}
                >
                  {isPublishing ? "Publishing..." : "Publish to Website"}
                </button>
              )}

              {(selectedRecipe.featured_image_r2 || selectedRecipe.enhanced_image_url || selectedRecipe.original_image_url) && (
                <div className={styles.recipeImageSection}>
                  <label className={styles.sectionLabel}>Featured Image</label>
                  <img
                    src={selectedRecipe.featured_image_r2 || selectedRecipe.enhanced_image_url || selectedRecipe.original_image_url}
                    alt={selectedRecipe.name}
                    className={styles.recipeImage}
                  />
                </div>
              )}

              <p className={styles.recipeSummary}>{selectedRecipe.summary}</p>

              <div className={styles.copySection}>
                <h4>SEO Title</h4>
                <div className={styles.copyBox}>
                  <span>{selectedRecipe.seo_title}</span>
                  <button onClick={() => navigator.clipboard.writeText(selectedRecipe.seo_title)}>
                    Copy
                  </button>
                </div>
              </div>

              <div className={styles.copySection}>
                <h4>Slug</h4>
                <div className={styles.copyBox}>
                  <code>{selectedRecipe.slug}</code>
                  <button onClick={() => navigator.clipboard.writeText(selectedRecipe.slug)}>
                    Copy
                  </button>
                </div>
              </div>

              <div className={styles.copySection}>
                <h4>Pinterest Title</h4>
                <div className={styles.copyBox}>
                  <span>{selectedRecipe.pinterest_title}</span>
                  <button onClick={() => navigator.clipboard.writeText(selectedRecipe.pinterest_title)}>
                    Copy
                  </button>
                </div>
              </div>

              <div className={styles.copySection}>
                <h4>Pinterest Description</h4>
                <div className={styles.copyBox}>
                  <span>{selectedRecipe.pinterest_description}</span>
                  <button onClick={() => navigator.clipboard.writeText(selectedRecipe.pinterest_description)}>
                    Copy
                  </button>
                </div>
              </div>

              <details className={styles.expandSection}>
                <summary>View Article HTML</summary>
                <div
                  className={styles.articlePreview}
                  dangerouslySetInnerHTML={{ __html: selectedRecipe.article_html }}
                />
                <button
                  onClick={() => navigator.clipboard.writeText(selectedRecipe.article_html)}
                  className={styles.copyFullBtn}
                >
                  Copy HTML
                </button>
              </details>

              <details className={styles.expandSection}>
                <summary>View Recipe JSON</summary>
                <pre className={styles.jsonViewer}>{selectedRecipe.recipe_json}</pre>
                <button
                  onClick={() => navigator.clipboard.writeText(selectedRecipe.recipe_json)}
                  className={styles.copyFullBtn}
                >
                  Copy JSON
                </button>
              </details>

              <button
                onClick={() => handleDeleteRecipe(selectedRecipe.id)}
                className={styles.deleteBtn}
              >
                Delete Recipe
              </button>
            </div>
          )}
        </aside>
      )}

      {/* Floating Image Preview on Hover */}
      {hoverImageUrl && (
        <div
          className={styles.imagePreview}
          style={{
            left: Math.min(hoverImagePos.x, window.innerWidth - 420),
            top: Math.max(10, Math.min(hoverImagePos.y, window.innerHeight - 420)),
          }}
        >
          <img src={hoverImageUrl} alt="Preview" />
        </div>
      )}

      {/* Payhip Local Browser Modal */}
      {showPayhipLocal && (
        <div className={styles.modal} onClick={() => !isPostingPayhip && closePayhipLocal()}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: "600px", width: "90%" }}>
            <h2 style={{ marginBottom: "1rem", display: "flex", alignItems: "center", gap: "10px" }}>
              {isPostingPayhip && <span className={styles.spinner} />}
              🖥️ Local Browser Control
              <button onClick={closePayhipLocal} style={{ marginLeft: "auto", background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer" }}>×</button>
            </h2>

            <div style={{ padding: "20px", background: "#f5f5f5", borderRadius: "8px", marginBottom: "20px" }}>
              <p style={{ margin: 0, fontSize: "1rem" }}>
                <strong>Status:</strong> {payhipLocalStatus || "Ready"}
              </p>
              {payhipLocalConnected && (
                <p style={{ margin: "10px 0 0", color: "#22c55e", fontSize: "0.9rem" }}>
                  ✓ Connected to local server
                </p>
              )}
            </div>

            <p style={{ color: "#666", fontSize: "0.9rem", marginBottom: "20px" }}>
              A browser window should have opened on your computer. You can:
              <br />• Watch the automation in real-time
              <br />• Solve Cloudflare captchas if they appear
              <br />• Make manual edits before submitting
            </p>

            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                onClick={closePayhipLocal}
                style={{ padding: "10px 20px", background: "#666", color: "white", border: "none", borderRadius: "6px", cursor: "pointer" }}
              >
                Close Browser
              </button>
              <button
                onClick={submitPayhipLocal}
                disabled={!payhipLocalConnected || !payhipLocalStatus.includes("Form filled")}
                style={{
                  padding: "10px 20px",
                  background: payhipLocalStatus.includes("Form filled") ? "#22c55e" : "#ccc",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: payhipLocalStatus.includes("Form filled") ? "pointer" : "not-allowed",
                  fontWeight: "bold",
                }}
              >
                Submit Form
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dual Image Overlay Modal */}
      {dualImageModal && (
        <div className={styles.modal} onClick={() => setDualImageModal(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: "700px" }}>
            <h2 style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              Dual Image Overlay
              <button onClick={() => setDualImageModal(null)} style={{ background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer" }}>×</button>
            </h2>

            <p style={{ color: "#666", marginBottom: "20px" }}>
              Select top and bottom images. They will be cropped to 1:1 center with text banner in the middle.
            </p>

            <div style={{ display: "flex", gap: "30px", marginBottom: "20px" }}>
              {/* Top Image Selection */}
              <div style={{ flex: 1 }}>
                <h3 style={{ marginBottom: "10px", fontSize: "0.9rem" }}>Top Image (blue = selected)</h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
                  {dualImageModal.candidates.map((c, idx) => (
                    <div
                      key={`top-${idx}`}
                      onClick={() => setDualImageModal({ ...dualImageModal, topImageUrl: c.url })}
                      style={{
                        cursor: "pointer",
                        border: dualImageModal.topImageUrl === c.url ? "3px solid #3b82f6" : "2px solid #ddd",
                        borderRadius: "8px",
                        overflow: "hidden",
                        aspectRatio: "1",
                      }}
                    >
                      <img src={c.url} alt={`Option ${idx + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Bottom Image Selection */}
              <div style={{ flex: 1 }}>
                <h3 style={{ marginBottom: "10px", fontSize: "0.9rem" }}>Bottom Image (green = selected)</h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
                  {dualImageModal.candidates.map((c, idx) => (
                    <div
                      key={`bottom-${idx}`}
                      onClick={() => setDualImageModal({ ...dualImageModal, bottomImageUrl: c.url })}
                      style={{
                        cursor: "pointer",
                        border: dualImageModal.bottomImageUrl === c.url ? "3px solid #22c55e" : "2px solid #ddd",
                        borderRadius: "8px",
                        overflow: "hidden",
                        aspectRatio: "1",
                      }}
                    >
                      <img src={c.url} alt={`Option ${idx + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Banner Style Selector */}
            <div style={{ marginBottom: "20px" }}>
              <h3 style={{ marginBottom: "10px", fontSize: "0.9rem" }}>Banner Style</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {[
                  { value: "random", label: "Random" },
                  { value: "dark_cocoa_gold", label: "Dark Cocoa Gold" },
                  { value: "soft_peach", label: "Soft Peach" },
                  { value: "dark_mocha", label: "Dark Mocha" },
                  { value: "retro_diner", label: "Retro Diner" },
                  { value: "blackberry_cream", label: "Blackberry" },
                  { value: "classic_cream", label: "Classic Cream" },
                ].map((style) => (
                  <button
                    key={style.value}
                    onClick={() => setDualImageModal({ ...dualImageModal, bannerStyle: style.value })}
                    style={{
                      padding: "6px 12px",
                      fontSize: "12px",
                      border: dualImageModal.bannerStyle === style.value ? "2px solid #3b82f6" : "1px solid #ddd",
                      borderRadius: "4px",
                      background: dualImageModal.bannerStyle === style.value ? "#e0f0ff" : "#fff",
                      cursor: "pointer",
                      fontWeight: dualImageModal.bannerStyle === style.value ? "bold" : "normal",
                    }}
                  >
                    {style.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <h3 style={{ marginBottom: "10px", fontSize: "0.9rem" }}>Preview Layout</h3>
              <div style={{ display: "inline-flex", flexDirection: "column", gap: "4px", width: "150px" }}>
                <div style={{ aspectRatio: "1", background: "#f0f0f0", borderRadius: "4px", overflow: "hidden" }}>
                  <img src={dualImageModal.topImageUrl} alt="Top" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
                <div style={{ background: "#fff", padding: "8px 4px", textAlign: "center", fontSize: "10px", fontWeight: "bold", border: "1px solid #ddd" }}>
                  Recipe Name Banner
                </div>
                <div style={{ aspectRatio: "1", background: "#f0f0f0", borderRadius: "4px", overflow: "hidden" }}>
                  <img src={dualImageModal.bottomImageUrl} alt="Bottom" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setDualImageModal(null)}
                style={{ padding: "10px 20px", background: "#666", color: "white", border: "none", borderRadius: "6px", cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateDualImageOverlay}
                disabled={isGeneratingOverlay}
                style={{
                  padding: "10px 20px",
                  background: isGeneratingOverlay ? "#ccc" : "#3b82f6",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: isGeneratingOverlay ? "not-allowed" : "pointer",
                  fontWeight: "bold",
                }}
              >
                {isGeneratingOverlay ? "Generating..." : "Generate Dual Overlay"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
